// Orchestration3D.tsx
// Drop-in replacement implementing cinematic LOD + lazy blossom internals + stability fixes.
//
// What this does:
// - LOD 0 (Galaxy): only Orchestrator + Blossom roots + Agents. No labels, no internals, no links, no particles.

// - LOD 1 (Regional): Orchestrator + Blossom roots + Agents + ONLY Orch->Blossom links (very faint). No labels.
// - LOD 2 (Detail): adds internals, but only for blossoms you zoom into (lazy loaded). Labels on. Particles gated.
// - Locks the ForceGraph simulation so nodes don't keep breathing.
// - Removes pulsing at far zoom and hides additive layers at distance to stop flicker.
// - Keeps a "hot mass" vibe zoomed out by rendering blossoms as glow-points only.

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import type { ForceGraphMethods } from 'react-force-graph-3d';
import * as THREE from 'three';
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
// Slider import removed — stepper UI has been replaced by free-play animation
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import useOsDark from '@/OS/hooks/useOsDark';

import { Pause, Play, RotateCcw, ZoomIn, ZoomOut, Home, Maximize2 } from 'lucide-react';
import ThinkingDots from '@/components/blueprints/ThinkingDots';
import { createScoutMesh, pickNextTarget, shouldForkBlossom, makeForkQuestion, hashToSeed, type ScoutState } from '@/components/blueprints/autoBlossomScout';
import { createSemanticForceEngine } from '@/forces/SemanticForceEngine';
import { generateEmbedding384 } from '@/lib/embeddings';


// =====================================================
// TEXTURE HELPERS
// =====================================================
let __glowTexture: THREE.Texture | null = null;
function getGlowTexture() {
  if (__glowTexture) return __glowTexture;
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.08, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.2, 'rgba(255,255,255,0.7)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.3)');
  g.addColorStop(0.65, 'rgba(255,255,255,0.08)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  __glowTexture = tex;
  return tex;
}

const __labelTexCache = new Map<string, THREE.Texture>();
function getLabelTexture(text: string) {
  const key = text;
  const cached = __labelTexCache.get(key);
  if (cached) return cached;

  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const fontPx = 18;
  const padX = 12;
  const padY = 10;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = `600 ${fontPx}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;
  const w = Math.ceil(ctx.measureText(text).width + padX * 2);
  const h = Math.ceil(fontPx + padY * 2);

  canvas.width = Math.ceil(w * dpr);
  canvas.height = Math.ceil(h * dpr);

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  ctx.fillStyle = 'rgba(20,25,40,0.78)';
  const r = 9;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(w, 0, w, h, r);
  ctx.arcTo(w, h, 0, h, r);
  ctx.arcTo(0, h, 0, 0, r);
  ctx.arcTo(0, 0, w, 0, r);
  ctx.closePath();
  ctx.fill();

  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 6;
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, padX, h / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;

  __labelTexCache.set(key, tex);
  return tex;
}

let __singlePointGeo: THREE.BufferGeometry | null = null;
function getSinglePointGeo() {
  if (__singlePointGeo) return __singlePointGeo;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0], 3));
  __singlePointGeo = g;
  return g;
}

function makeLabelSprite(text: string) {

  const tex = getLabelTexture(text);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
      fog: false,
    })
  );

  const img = tex.image as any;
  const aspect = ((img?.width as number) || 1) / ((img?.height as number) || 1);
  sprite.userData.__isLabel = true;
  sprite.userData.__aspect = aspect;
  sprite.userData.__base = 7;
  sprite.renderOrder = 999;
  return sprite;
}

// =====================================================
// TYPES
// =====================================================
export type OrchestrationEvent = {
  ts: number;
  from: string;
  to: string;
  status: 'success' | 'failure';
  latencyMs?: number;
  text: string;
  role?: 'agent' | 'tool' | 'system';
  sources?: { title: string; path?: string; snippet?: string }[];
  kind?: 'message' | 'process' | 'blossom' | 'fork';
};

export type OrchestrationNodeType = 'orchestrator' | 'agent' | 'tool' | 'kb' | 'process' | 'blossom';

export type OrchestrationNode = {
  id: string;
  type: OrchestrationNodeType;
  label: string;
  cluster?: string;
  error?: boolean;
  queueDepth?: number;
  moduleOf?: string;
  kbOf?: string;
};

type GraphNode = OrchestrationNode & {
  x?: number;
  y?: number;
  z?: number;
  fx?: number;
  fy?: number;
  fz?: number;
  // Semantic physics extensions (optional)
  mass?: number;
  affinity?: Record<string, number>;
  isAnchor?: boolean;
  description?: string;
};

type GraphLink = {
  source: string;
  target: string;
  status: 'success' | 'failure';
  latencyMs?: number;
  lastText?: string;
  kind?: 'message' | 'process' | 'blossom' | 'fork';
};

type GraphSnapshot = { nodes: GraphNode[]; links: GraphLink[] };

type SlotMaps = {
  mainBlossomSlots: Map<string, number>;
  nextMainBlossomSlot: { current: number };
  forkBlossomSlots: Map<string, Map<string, number>>;
  procSlots: Map<string, Map<string, number>>;
  agentSlots: Map<string, Map<string, number>>;
};

type SemanticConfig = {
  enabled: boolean;
  temp: number;
  G: number;
  K: number;
  topK: number;
  affinityThreshold: number;
  anchorEnabled: boolean;
  promptText: string;
};

type Props = {
  nodes: OrchestrationNode[];
  events: OrchestrationEvent[];
  eventCursor?: number;
  className?: string;
  /** When true, show "alive" energy animations on the orchestrator (used for live runs). */
  live?: boolean;
  /** When true, show the Auto-Blossom scout (blue orb) roaming between far blossoms. */
  autoBlossom?: boolean;
  /** Keywords the scout should prioritize when choosing targets. */
  scoutKeywords?: string[];
  /** Optional external semantic settings (e.g. from the Run screen). When present, it becomes the source of truth. */
  semanticConfig?: SemanticConfig;
};

// =====================================================
// MATH / COLOR
// =====================================================
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
function colorForNodeType(t: OrchestrationNodeType) {
  if (t === 'orchestrator') return '#c084fc';
  if (t === 'blossom') return '#fbbf24';
  if (t === 'agent') return '#60a5fa';
  if (t === 'tool') return '#34d399';
  if (t === 'process') return '#f472b6';
  return '#fbbf24';
}
function isPromptAnchor(node: any) {
  return Boolean(node?.isAnchor) && String(node?.id) === 'prompt';
}

function hashColor(input: string) {
  const palette = ['#60a5fa', '#a855f7', '#34d399', '#fbbf24', '#22c55e', '#fb7185', '#06b6d4', '#f97316'];
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}
function colorForNode(node: OrchestrationNode) {
  if (isPromptAnchor(node as any)) return '#22c55e';
  if (node.type === 'process') {
    const key = String(node.cluster || node.label || node.id);
    return hashColor(key);
  }
  return colorForNodeType(node.type);
}

function defaultNodeSize(n: OrchestrationNode) {
  if (isPromptAnchor(n as any)) return 10;
  if (n.type === 'orchestrator') return 10;
  if (n.type === 'blossom') return 7;
  if (n.type === 'agent') return 6;
  if (n.type === 'kb') return 4;
  if (n.type === 'process') return 3.5;
  return 3.5;
}

function colorForLink(status: 'success' | 'failure', kind?: OrchestrationEvent['kind']) {
  if (kind === 'blossom') return '#fbbf24';
  if (kind === 'fork') return '#818cf8';
  return status === 'failure' ? '#ef4444' : '#4ade80';
}

function inferOrchestrationFromTheaterHistory(history: any[]) {
  // Minimal inference from existing Agent Theater events.
  // - agent lanes become agent nodes
  // - system/tool messages attach to orchestrator
  // - blossom events create a Blossom root far from the core
  // - fork events create a new Orchestrator that branches from a blossom
  // - Blossom root anchors its KB + processes (internal machinery)
  const agents = new Set<string>();
  const kbTitles = new Map<string, { kbId: string; title: string; blossomId: string; moduleOf: string }>();
  const blossomByNodeId = new Map<string, { blossomId: string; kbId: string; title: string }>();
  const forkOrchestrators = new Map<string, { id: string; label: string; moduleOf: string }>();
  const processNodes = new Map<string, { id: string; label: string; cluster: string; moduleOf: string }>();

  const events: OrchestrationEvent[] = [];

  const ORCH = 'orchestrator';

  // Keep last speaker per cluster (so separate blossoms don't cross-wire)
  const lastAgentByCluster = new Map<string, string>();

  const getClusterKey = (h: any) => {
    const rawNodeId = String(h?.nodeId || 'main');
    if (rawNodeId.startsWith('blossom:')) return rawNodeId;
    if (rawNodeId.startsWith('orch:')) return rawNodeId;
    return 'main';
  };

  for (let i = 0; i < history.length; i++) {
    const h = history[i];
    const role = h.role || 'system';
    const lane = String(h.lane || 'left');
    const isAgent = role === 'agent' && lane !== 'left';

    const cluster = getClusterKey(h);

    // Fork events: blossom -> new orchestrator
    if (String(h.kind || '') === 'fork') {
      const parent = String(h.parentBlossomId || h.fromBlossomId || h.blossomId || ORCH);
      const orchId = String(h.orchestratorId || h.nodeId || `orch:${i}`);
      const title = String(h.title || h.question || h.blossomTitle || h.message || orchId);

      forkOrchestrators.set(orchId, { id: orchId, label: title, moduleOf: parent });

      events.push({
        ts: Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i,
        from: parent,
        to: orchId,
        status: 'success',
        latencyMs: 220,
        text: String(h.message || `Forked orchestrator: ${title}`),
        role: 'system',
        kind: 'fork',
      });

      continue;
    }

    // Blossom events (emitted by the Blossom engine)
    if (String(h.kind || '') === 'blossom') {
      const moduleOf = String(h.orchestratorId || ORCH);
      const title = String(h.kbTitle || h.kbId || h.message || `kb:${i}`);

      // IMPORTANT: keep blossoms unique per orchestrator so fork outposts can have their own blossoms.
      const kbKey = `${moduleOf}::${title}`;

      if (!kbTitles.has(kbKey)) {
        const kbId = `kb:${kbTitles.size + 1}`;
        const blossomId = `blossom:${kbId}`;
        kbTitles.set(kbKey, { kbId, title, blossomId, moduleOf });
      }
      const { kbId, blossomId } = kbTitles.get(kbKey)!;

      blossomByNodeId.set(String(h.nodeId || blossomId), { blossomId, kbId, title });

      // Orchestrator spawns a Blossom root (outer ring)
      events.push({
        ts: Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i,
        from: moduleOf,
        to: blossomId,
        status: 'success',
        latencyMs: 120,
        text: String(h.message || 'Blossom'),
        role: 'system',
        kind: 'blossom',
      });

      // Blossom root owns the KB node (inner machinery)
      events.push({
        ts: (Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i) + 1,
        from: blossomId,
        to: kbId,
        status: 'success',
        latencyMs: 80,
        text: `KB attached: ${title}`,
        role: 'system',
        kind: 'process',
      });

      // also create a process node representing the blossom run, attached to the Blossom root
      const pid = String(h.nodeId || `blossom-${kbId}`);
      const procId = `proc:${pid}`;
      if (!processNodes.has(procId)) {
        processNodes.set(procId, { id: procId, label: pid, cluster: blossomId, moduleOf: blossomId });
        events.push({
          ts: (Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i) + 2,
          from: blossomId,
          to: procId,
          status: 'success',
          latencyMs: 140,
          text: `Process created: ${pid}`,
          role: 'system',
          kind: 'process',
        });
      }

      continue;
    }

    // Deep-research-seeds events: the executor persisted seeds in history so they
    // survive DB round-trips. Expand each seed into a blossom node + link, exactly
    // like a real kind:'blossom' event would, so the Cortex renders them immediately
    // without requiring the blossom pump to have run.
    if (String(h.kind || '') === 'deep-research-seeds' && Array.isArray(h.seeds)) {
      const moduleOf = String(h.orchestratorId || ORCH);
      const baseTs = Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i;

      for (let si = 0; si < h.seeds.length; si++) {
        const seed = h.seeds[si];
        const title = String(seed?.title || '').trim();
        const url = String(seed?.url || '').trim();
        if (!title || !url) continue;

        const kbKey = `${moduleOf}::${title}`;
        if (!kbTitles.has(kbKey)) {
          const kbId = `kb:${kbTitles.size + 1}`;
          const blossomId = `blossom:${kbId}`;
          kbTitles.set(kbKey, { kbId, title, blossomId, moduleOf });
        }
        const { kbId, blossomId } = kbTitles.get(kbKey)!;

        blossomByNodeId.set(`blossom:${title}`, { blossomId, kbId, title });

        events.push({
          ts: baseTs + si * 3,
          from: moduleOf,
          to: blossomId,
          status: 'success',
          latencyMs: 120,
          text: `Blossom: ${title}`,
          role: 'system',
          kind: 'blossom',
        });

        events.push({
          ts: baseTs + si * 3 + 1,
          from: blossomId,
          to: kbId,
          status: 'success',
          latencyMs: 80,
          text: `KB attached: ${title}`,
          role: 'system',
          kind: 'process',
        });
      }

      continue;
    }

    // Process node inferred from nodeId
    const pid = String(h.nodeId || 'unknown');
    const procId = `proc:${pid}`;
    if (!processNodes.has(procId)) {
      const clusterName = pid.split('-')[0] || 'process';
      const moduleOf = String(h.orchestratorId || blossomByNodeId.get(pid)?.blossomId || ORCH);
      processNodes.set(procId, { id: procId, label: pid, cluster: clusterName, moduleOf });
      // orchestrator (or blossom) spawns process nodes over time
      events.push({
        ts: Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i,
        from: moduleOf,
        to: procId,
        status: 'success',
        latencyMs: 220,
        text: `Process created: ${pid}`,
        role: 'system',
        kind: 'process',
      });
    }

    if (isAgent) {
      agents.add(lane);
      const lastAgent = lastAgentByCluster.get(cluster) || null;
      if (lastAgent && lastAgent !== lane) {
        // derive an A -> B event
        const latencyMs = 250 + (h.message?.length ? Math.min(1800, h.message.length * 4) : 400);
        const status: 'success' | 'failure' = String(h.message || '').toLowerCase().includes('error') ? 'failure' : 'success';
        events.push({
          ts: Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i,
          from: lastAgent,
          to: lane,
          status,
          latencyMs,
          text: String(h.message || ''),
          role: 'agent',
          sources: Array.isArray(h.sources) ? h.sources.map((s: any) => ({ title: s.title, path: s.path, snippet: s.snippet })) : undefined,
          kind: 'message',
        });
      }
      lastAgentByCluster.set(cluster, lane);
    } else {
      // route non-agent messages through orchestrator (or through blossom root if applicable)
      const lastAgent = lastAgentByCluster.get(cluster) || null;
      const moduleOf = String(h.orchestratorId || blossomByNodeId.get(String(h.nodeId || ''))?.blossomId || ORCH);
      const target = lastAgent || moduleOf;
      const status: 'success' | 'failure' = role === 'system' && String(h.message || '').toLowerCase().includes('error') ? 'failure' : 'success';
      const latencyMs = 160 + (h.message?.length ? Math.min(1200, h.message.length * 2) : 250);
      events.push({
        ts: Date.parse(h.timestamp || new Date().toISOString()) || Date.now() + i,
        from: moduleOf,
        to: target,
        status,
        latencyMs,
        text: String(h.message || ''),
        role,
        sources: Array.isArray(h.sources) ? h.sources.map((s: any) => ({ title: s.title, path: s.path, snippet: s.snippet })) : undefined,
        kind: 'message',
      });
    }
  }

  // Nodes: main orchestrator + fork orchestrators + agents + blossom roots + KBs + process nodes
  const nodes: OrchestrationNode[] = [
    { id: ORCH, type: 'orchestrator', label: 'Orchestrator', queueDepth: 0 },
    ...Array.from(forkOrchestrators.values()).map((o) => ({
      id: o.id,
      type: 'orchestrator' as const,
      label: String(o.label || o.id),
      moduleOf: o.moduleOf,
      queueDepth: 0,
    })),
    ...Array.from(agents).map((id) => ({
      id,
      type: 'agent' as const,
      label: id.split('/').slice(-1)[0] || id,
      // mark error if any failure event points to it
      error: events.some((e) => e.status === 'failure' && (e.from === id || e.to === id)),
      queueDepth: 0,
    })),
    ...Array.from(kbTitles.values()).map((kb) => ({
      id: kb.blossomId,
      type: 'blossom' as const,
      label: kb.title,
      moduleOf: kb.moduleOf,
    })),
    ...Array.from(kbTitles.values()).map((kb) => ({
      id: kb.kbId,
      type: 'kb' as const,
      label: kb.title,
      kbOf: kb.blossomId,
    })),
    ...Array.from(processNodes.values()).map((p) => ({
      id: p.id,
      type: 'process' as const,
      label: p.label,
      cluster: p.cluster,
      moduleOf: p.moduleOf,
      queueDepth: 0,
    })),
  ];

  // Add tool modules when we see tool role messages.
  const toolCount = events.filter((e) => e.role === 'tool').length;
  if (toolCount) {
    const toolNode: OrchestrationNode = {
      id: 'tools',
      type: 'tool',
      label: 'Tools',
      moduleOf: ORCH,
      queueDepth: 0,
    };
    nodes.push(toolNode);
  }

  return { nodes, events };
}

// =====================================================
// LOD DEFINITIONS
// =====================================================
type LODLevel = 0 | 1 | 2;

// galaxy/regional/detail distance thresholds (to ORCHESTRATOR)
const LOD_GALAXY_DIST = 2600; // > => LOD 0
const LOD_REGIONAL_DIST = 1500; // > => LOD 1, else LOD 2

// lazy load blossom internals when camera is near that blossom root
const BLOSSOM_LOAD_DIST = 520;

// hide expensive additive layers farther than this (per-object distance to camera)
const HIDE_GLOW_DIST = 1600;
const HIDE_HALO_DIST = 1100;

// allow pulsing only when close
const PULSE_DIST = 420;

// =====================================================
// GRAPH BUILDING
// =====================================================
function buildLinkMap(events: OrchestrationEvent[], cursor: number) {
  const visible = events.slice(0, clamp(cursor, 0, events.length));
  const linkMap = new Map<string, GraphLink>();
  for (const e of visible) {
    const key = `${e.from}::${e.to}::${e.kind || 'message'}`;
    linkMap.set(key, {
      source: e.from,
      target: e.to,
      status: e.status,
      latencyMs: e.latencyMs,
      lastText: e.text,
      kind: (e.kind || 'message') as any,
    });
  }
  return { linksAll: Array.from(linkMap.values()), visibleEvents: visible };
}

function partitionNodes(nodes: OrchestrationNode[]) {
  const byId = new Map<string, OrchestrationNode>();
  for (const n of nodes) byId.set(String(n.id), n);

  const orchestrator = nodes.find((n) => n.type === 'orchestrator') || { id: 'orchestrator', type: 'orchestrator', label: 'Orchestrator' };

  const blossoms = nodes.filter((n) => n.type === 'blossom');
  const agents = nodes.filter((n) => n.type === 'agent');
  const tools = nodes.filter((n) => n.type === 'tool');

  // internal nodes: kb + process (and optionally tools if you want them hidden until detail)
  const kb = nodes.filter((n) => n.type === 'kb');
  const proc = nodes.filter((n) => n.type === 'process');

  // Index internals by blossom root id
  const kbByBlossom = new Map<string, OrchestrationNode[]>();
  for (const k of kb) {
    const parent = String(k.kbOf || '');
    if (!parent) continue;
    kbByBlossom.set(parent, [...(kbByBlossom.get(parent) || []), k]);
  }

  const procByParent = new Map<string, OrchestrationNode[]>();
  for (const p of proc) {
    const parent = String(p.moduleOf || 'orchestrator');
    procByParent.set(parent, [...(procByParent.get(parent) || []), p]);
  }

  return { byId, orchestrator, blossoms, agents, tools, kbByBlossom, procByParent };
}

function buildVisibleGraph({
  base,
  linksAll,
  lod,
  loadedBlossoms,
}: {
  base: ReturnType<typeof partitionNodes>;
  linksAll: GraphLink[];
  lod: LODLevel;
  loadedBlossoms: Set<string>;
}) {
  // Nodes
  const visibleNodes: OrchestrationNode[] = [];
  visibleNodes.push(base.orchestrator);

  // Always show agents + blossoms
  visibleNodes.push(...base.agents);
  visibleNodes.push(...base.blossoms);

  // LOD 2: show tools + internals for loaded blossoms + orchestrator internals
  if (lod === 2) {
    // tools are global; show at detail
    visibleNodes.push(...base.tools);

    // processes attached to orchestrator (moduleOf=orchestrator)
    const orchProcs = base.procByParent.get('orchestrator') || [];
    visibleNodes.push(...orchProcs);

    // For each loaded blossom: add kb + processes attached to that blossom
    for (const bid of loadedBlossoms) {
      const kbs = base.kbByBlossom.get(bid) || [];
      visibleNodes.push(...kbs);

      const procs = base.procByParent.get(bid) || [];
      visibleNodes.push(...procs);
    }
  }

  // Links
  let visibleLinks: GraphLink[] = [];

  if (lod === 0) {
    // Galaxy: no links at all
    visibleLinks = [];
  } else if (lod === 1) {
    // Regional: only blossom birth links Orch->Blossom
    visibleLinks = linksAll.filter((l) => l.kind === 'blossom');
  } else {
    // Detail: all links, but you can optionally drop process links for unloaded blossoms
    visibleLinks = linksAll.filter((l) => {
      // keep everything that touches visible nodes
      return true;
    });
  }

  // Deduplicate nodes by id
  const seen = new Set<string>();
  const dedupedNodes = visibleNodes.filter((n) => {
    const id = String(n.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  // Filter links to only those whose endpoints exist
  const nodeSet = new Set(dedupedNodes.map((n) => String(n.id)));
  visibleLinks = visibleLinks.filter((l) => nodeSet.has(String(l.source)) && nodeSet.has(String(l.target)));

  return { nodes: dedupedNodes as GraphNode[], links: visibleLinks };
}

function buildFullGraph(base: ReturnType<typeof partitionNodes>, linksAll: GraphLink[]) {
  const orchestrators = Array.from(base.byId.values()).filter((n) => n.type === 'orchestrator');

  const visibleNodes: OrchestrationNode[] = [
    ...orchestrators,
    ...base.agents,
    ...base.blossoms,
    ...base.tools,
    ...(base.procByParent.get('orchestrator') || []),
    ...Array.from(base.kbByBlossom.values()).flat(),
    ...Array.from(base.procByParent.values()).flat(),
  ];

  const seen = new Set<string>();
  const nodes = visibleNodes.filter((n) => {
    const id = String(n.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }) as GraphNode[];

  const nodeSet = new Set(nodes.map((n) => String(n.id)));
  const links = linksAll.filter((l) => nodeSet.has(String(l.source)) && nodeSet.has(String(l.target)));

  return { nodes, links };
}

/** Extract node ID from a value that may be a string or a ForceGraph3D-mutated object ref */
function getLinkEndpointId(val: any): string {
  if (val == null) return '';
  if (typeof val === 'object') return String(val.id ?? '');
  return String(val);
}

type StablePool = {
  nodeMap: Map<string, any>;   // id → stable node object
  linkMap: Map<string, any>;   // "source::target::kind" → stable link object
};

/**
 * Merge a raw graph snapshot into a stable pool, preserving object references.
 * In live mode (liveMode=true), nodes/links are only ADDED, never removed.
 * This prevents ForceGraph3D's ThreeDigest2 from encountering null entries
 * during rapid add/remove cycles.
 *
 * Returns { nodes, links } with new array refs (so ForceGraph3D detects change)
 * but the element refs are STABLE (same objects as the pool).
 */
function mergeIntoStablePool(
  raw: GraphSnapshot,
  pool: StablePool,
  liveMode: boolean
): GraphSnapshot {
  const incomingIds = new Set<string>();

  // 1. Merge nodes: update existing in-place, add new ones
  for (const n of raw.nodes || []) {
    if (!n || !String(n.id)) continue;
    const id = String(n.id);
    incomingIds.add(id);
    const existing = pool.nodeMap.get(id);
    if (existing) {
      // Update properties on the SAME object. Preserve ForceGraph3D-managed fields.
      for (const key of Object.keys(n)) {
        if (key === '__threeObj' || key === 'index' || key === 'vx' || key === 'vy' || key === 'vz') continue;
        // Don't overwrite positions that ForceGraph3D / prePositionNodes already set
        if ((key === 'x' || key === 'y' || key === 'z') && Number.isFinite(existing[key])) continue;
        (existing as any)[key] = (n as any)[key];
      }
    } else {
      pool.nodeMap.set(id, { ...n }); // clone for initial entry
    }
  }

  // In non-live mode, remove nodes no longer present
  if (!liveMode) {
    for (const id of pool.nodeMap.keys()) {
      if (!incomingIds.has(id)) pool.nodeMap.delete(id);
    }
  }

  // 2. Merge links: update existing in-place, add new ones
  const validNodeIds = new Set(pool.nodeMap.keys());
  const incomingLinkKeys = new Set<string>();

  for (const l of raw.links || []) {
    if (!l) continue;
    const sourceId = getLinkEndpointId(l.source);
    const targetId = getLinkEndpointId(l.target);
    if (!sourceId || !targetId || !validNodeIds.has(sourceId) || !validNodeIds.has(targetId)) continue;
    const key = `${sourceId}::${targetId}::${l.kind || 'message'}`;
    incomingLinkKeys.add(key);
    const existing = pool.linkMap.get(key);
    if (existing) {
      // Update props but DON'T overwrite source/target (ForceGraph3D mutated them to object refs)
      existing.status = l.status;
      existing.latencyMs = l.latencyMs;
      existing.lastText = l.lastText;
    } else {
      pool.linkMap.set(key, {
        source: sourceId,
        target: targetId,
        __linkId: key,           // Stable ID for ForceGraph3D (prevents index-based churn)
        status: l.status,
        latencyMs: l.latencyMs,
        lastText: l.lastText,
        kind: l.kind,
      });
    }
  }

  // In non-live mode, remove links no longer present
  if (!liveMode) {
    for (const key of pool.linkMap.keys()) {
      if (!incomingLinkKeys.has(key)) pool.linkMap.delete(key);
    }
  }

  // 3. Return new array refs (triggers ForceGraph3D prop change detection)
  //    but element refs are STABLE (same objects as pool)
  return {
    nodes: Array.from(pool.nodeMap.values()),
    links: Array.from(pool.linkMap.values()),
  };
}

// =====================================================
// PRE-POSITIONING (deterministic layout, pure function)
// =====================================================

const _mulberry32 = (a: number) => {
  return () => {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const _getOrAssignSlot = (map: Map<string, number>, key: string, nextRef: { current: number }) => {
  const existing = map.get(key);
  if (typeof existing === 'number') return existing;
  const idx = nextRef.current++;
  map.set(key, idx);
  return idx;
};

const _getOrAssignChildSlot = (parentMap: Map<string, Map<string, number>>, parentId: string, childId: string) => {
  let m = parentMap.get(parentId);
  if (!m) {
    m = new Map();
    parentMap.set(parentId, m);
  }
  const existing = m.get(childId);
  if (typeof existing === 'number') return existing;
  const idx = m.size;
  m.set(childId, idx);
  return idx;
};

/**
 * Assign deterministic fx/fy/fz + x/y/z to all nodes in a snapshot.
 * Same layout algorithm as the positioning useEffect, but runs BEFORE
 * ForceGraph3D sees the data so there is no visual snap/jerk.
 * Mutates the snapshot's nodes in-place (safe because mergeIntoStablePool created stable entries).
 */
function prePositionNodes(snapshot: GraphSnapshot, slots: SlotMaps): GraphSnapshot {
  const allNodes = snapshot.nodes as any[];
  if (!allNodes.length) return snapshot;

  const orch = allNodes.find((n: any) => String(n.id) === 'orchestrator');
  if (!orch) return snapshot;

  // Orchestrator at origin
  orch.fx = 0; orch.fy = 0; orch.fz = 0;
  orch.x = 0; orch.y = 0; orch.z = 0;

  const blossomNodes = allNodes.filter((n: any) => n.type === 'blossom');

  // Galaxy spiral arm layout
  const SPIRAL_ARMS = 3;
  const ARM_BASE_ANGLE = (Math.PI * 2) / SPIRAL_ARMS;
  const GOLDEN_ANGLE = 2.39996323;
  const BLOSSOM_BASE_RADIUS = 600;
  const BLOSSOM_SPACING = 350;
  const HEIGHT_AMPLITUDE = 280;

  const mainBlossoms = blossomNodes.filter((b: any) => String(b.moduleOf || 'orchestrator') === 'orchestrator');
  const forkBlossoms = blossomNodes.filter((b: any) => String(b.moduleOf || 'orchestrator') !== 'orchestrator');

  for (const b of mainBlossoms) {
    if (Number.isFinite(b.fx) && Number.isFinite(b.fy) && Number.isFinite(b.fz)) continue;
    const slot = _getOrAssignSlot(slots.mainBlossomSlots, String(b.id), slots.nextMainBlossomSlot);
    const arm = slot % SPIRAL_ARMS;
    const armSlot = Math.floor(slot / SPIRAL_ARMS);
    const angle = ARM_BASE_ANGLE * arm + armSlot * GOLDEN_ANGLE * 0.4;
    const radius = BLOSSOM_BASE_RADIUS + armSlot * BLOSSOM_SPACING;
    b.fx = Math.cos(angle) * radius;
    b.fz = Math.sin(angle) * radius;
    b.fy = Math.sin(angle * 0.7) * HEIGHT_AMPLITUDE * (0.3 + armSlot * 0.12) + ((slot % 5) - 2) * 80;
  }

  // Fork orchestrators
  const forkOrchs = allNodes.filter((n: any) => n.type === 'orchestrator' && String(n.id) !== 'orchestrator');
  for (const o of forkOrchs) {
    if (Number.isFinite(o.fx) && Number.isFinite(o.fy) && Number.isFinite(o.fz)) continue;
    const id = String(o.id);
    const seed = hashToSeed(id);
    const rng = _mulberry32(seed ^ 0x9e3779b9);
    const parentId = String(o.moduleOf || '');
    const parent = allNodes.find((n: any) => String(n.id) === parentId) || null;
    const px = Number(parent?.fx ?? parent?.x ?? 0);
    const py = Number(parent?.fy ?? parent?.y ?? 0);
    const pz = Number(parent?.fz ?? parent?.z ?? 0);
    const out = new THREE.Vector3(px, py * 0.35, pz);
    if (out.lengthSq() < 1e-6) {
      const a = rng() * Math.PI * 2;
      out.set(Math.cos(a), 0, Math.sin(a));
    }
    out.normalize();
    const side = new THREE.Vector3().crossVectors(out, new THREE.Vector3(0, 1, 0));
    if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
    side.normalize();
    const outward = 520 + rng() * 260;
    const lateral = (rng() - 0.5) * 320;
    const lift = (rng() - 0.5) * 260;
    o.fx = px + out.x * outward + side.x * lateral;
    o.fy = py + lift;
    o.fz = pz + out.z * outward + side.z * lateral;
  }

  // Fork blossoms
  for (const b of forkBlossoms) {
    if (Number.isFinite(b.fx) && Number.isFinite(b.fy) && Number.isFinite(b.fz)) continue;
    const parentId = String(b.moduleOf || 'orchestrator');
    const parent = allNodes.find((n: any) => String(n.id) === parentId);
    if (!parent) continue;
    const slot = _getOrAssignChildSlot(slots.forkBlossomSlots, parentId, String(b.id));
    const seed = hashToSeed(`fork-blossoms:${parentId}`);
    const rng = _mulberry32(seed);
    const baseR = 220 + rng() * 80;
    const yJitter = 80;
    const ang = (slot / Math.max(1, 6)) * Math.PI * 2 + rng() * 0.35;
    b.fx = (parent.fx ?? parent.x ?? 0) + Math.cos(ang) * baseR;
    b.fz = (parent.fz ?? parent.z ?? 0) + Math.sin(ang) * baseR;
    b.fy = (parent.fy ?? parent.y ?? 0) + ((slot % 3) - 1) * (yJitter * 0.35) + (rng() - 0.5) * yJitter;
  }

  // KB nodes
  for (const kb of allNodes.filter((n: any) => n.type === 'kb')) {
    if (Number.isFinite(kb.fx) && Number.isFinite(kb.fy) && Number.isFinite(kb.fz)) continue;
    const parentId = String(kb.kbOf || '');
    const parent = blossomNodes.find((n: any) => String(n.id) === parentId);
    if (!parent) continue;
    const o = 26;
    kb.fx = (parent.fx ?? parent.x ?? 0) + o;
    kb.fy = (parent.fy ?? parent.y ?? 0) - 12;
    kb.fz = (parent.fz ?? parent.z ?? 0) - o;
  }

  // Process nodes
  const procNodes = allNodes.filter((n: any) => n.type === 'process');
  const procsByParent = new Map<string, any[]>();
  for (const p of procNodes) {
    const parentId = String(p.moduleOf || 'orchestrator');
    procsByParent.set(parentId, [...(procsByParent.get(parentId) || []), p]);
  }
  for (const [parentId, list] of procsByParent.entries()) {
    const parent = allNodes.find((n: any) => String(n.id) === parentId) || orch;
    for (const p of list) {
      if (Number.isFinite(p.fx) && Number.isFinite(p.fy) && Number.isFinite(p.fz)) continue;
      const slot = _getOrAssignChildSlot(slots.procSlots, parentId, String(p.id));
      const ring = parent?.type === 'orchestrator' ? 66 : parent?.type === 'blossom' ? 72 : 54;
      const ang = (slot / Math.max(1, 10)) * Math.PI * 2;
      p.fx = (parent.fx ?? parent.x ?? 0) + Math.cos(ang) * ring;
      p.fy = (parent.fy ?? parent.y ?? 0) - (parent?.type === 'orchestrator' ? 22 : 18) + (slot % 3) * 5;
      p.fz = (parent.fz ?? parent.z ?? 0) + Math.sin(ang) * ring;
    }
  }

  // Agent nodes
  const agentNodes = allNodes.filter((n: any) => n.type === 'agent');
  const agentsByParent = new Map<string, any[]>();
  for (const a of agentNodes) {
    const id = String(a.id || '');
    const parts = id.split('/');
    const maybeParentId = parts.length > 1 ? parts[0] : '';
    const parent = (maybeParentId && allNodes.find((n: any) => String(n.id) === maybeParentId)) || orch;
    const parentId = String(parent?.id || 'orchestrator');
    agentsByParent.set(parentId, [...(agentsByParent.get(parentId) || []), a]);
  }
  for (const [parentId, list] of agentsByParent.entries()) {
    const parent = allNodes.find((n: any) => String(n.id) === parentId) || orch;
    for (const a of list) {
      if (Number.isFinite(a.fx) && Number.isFinite(a.fy) && Number.isFinite(a.fz)) continue;
      const slot = _getOrAssignChildSlot(slots.agentSlots, parentId, String(a.id));
      const ring = parent?.type === 'orchestrator' ? 102 : parent?.type === 'blossom' ? 94 : 78;
      const ang = (slot / Math.max(1, 10)) * Math.PI * 2;
      a.fx = (parent.fx ?? parent.x ?? 0) + Math.cos(ang) * ring;
      a.fy = (parent.fy ?? parent.y ?? 0) + 12 + ((slot % 3) - 1) * 6;
      a.fz = (parent.fz ?? parent.z ?? 0) + Math.sin(ang) * ring;
    }
  }

  // Tool nodes
  for (const t of allNodes.filter((n: any) => n.type === 'tool')) {
    if (Number.isFinite(t.fx) && Number.isFinite(t.fy) && Number.isFinite(t.fz)) continue;
    t.fx = 22; t.fy = -6; t.fz = 18;
  }

  // Snap x/y/z to fx/fy/fz for newly placed nodes
  for (const n of allNodes) {
    if (Number.isFinite(n.fx) && !Number.isFinite(n.x)) n.x = n.fx;
    if (Number.isFinite(n.fy) && !Number.isFinite(n.y)) n.y = n.fy;
    if (Number.isFinite(n.fz) && !Number.isFinite(n.z)) n.z = n.fz;
  }

  return snapshot;
}

// =====================================================
// NODE RENDERING
// =====================================================

type VisCache = {
  cores: THREE.Object3D[];
  labels: THREE.Object3D[];
  halos: THREE.Object3D[];
  glowPoints: THREE.Object3D[];
  massClouds: THREE.Object3D[];
  orbits: THREE.Object3D[];
  queueRings: THREE.Object3D[];
  orchRings: THREE.Object3D[];
  orchShells: THREE.Object3D[];
  toolGlows: THREE.Object3D[];
  errorGlows: THREE.Object3D[];
};

function ensureBlossomMassCloud(group: THREE.Group) {
  const u: any = group.userData || {};
  const vis = u.__vis as VisCache | undefined;
  if (vis?.massClouds?.length) return;

  const cloudGeo = new THREE.BufferGeometry();
  const count = 120;
  const positions = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    const r = 18 + Math.random() * 35;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);

    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }

  cloudGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const cloudMat = new THREE.PointsMaterial({
    map: getGlowTexture(),
    size: 5,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.18,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
  });

  const cloud = new THREE.Points(cloudGeo, cloudMat);
  cloud.userData.__isMassCloud = true;
  cloud.renderOrder = 0;
  group.add(cloud);

  if (vis) vis.massClouds.push(cloud);
}


// =====================================================
// MULTI-LAYER STARFIELD + NEBULA
// =====================================================
function ensureStarfield(scene: THREE.Scene) {
  const existing = scene.getObjectByName('orch-stars-group') as any;
  if (existing) return existing as THREE.Group;

  const group = new THREE.Group();
  group.name = 'orch-stars-group';
  group.frustumCulled = false;
  group.renderOrder = -1000;

  const disposables: { dispose: () => void }[] = [];

  // --- Layer 1: Far stars (4000, small, white/blue tint) ---
  {
    const count = 4000;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 2000 + Math.random() * 3000;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
      const b = 0.8 + Math.random() * 0.2;
      col[i * 3] = b * 0.88;
      col[i * 3 + 1] = b * 0.91;
      col[i * 3 + 2] = b;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    const mat = new THREE.PointsMaterial({
      size: 1.5,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      vertexColors: true,
      depthWrite: false,
      depthTest: false,
    });
    const layer = new THREE.Points(geo, mat);
    layer.frustumCulled = false;
    group.add(layer);
    disposables.push(geo, mat);
  }

  // --- Layer 2: Mid stars (1200, colored tints — blue/gold/rose/purple) ---
  {
    const count = 1200;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const tints = [
      [0.6, 0.75, 1.0],
      [1.0, 0.85, 0.5],
      [1.0, 0.6, 0.7],
      [0.75, 0.5, 1.0],
    ];
    for (let i = 0; i < count; i++) {
      const r = 1200 + Math.random() * 1800;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
      const tint = tints[Math.floor(Math.random() * tints.length)];
      const bright = 0.7 + Math.random() * 0.3;
      col[i * 3] = tint[0] * bright;
      col[i * 3 + 1] = tint[1] * bright;
      col[i * 3 + 2] = tint[2] * bright;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    const mat = new THREE.PointsMaterial({
      size: 2.5,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.9,
      vertexColors: true,
      map: getGlowTexture(),
      depthWrite: false,
      depthTest: false,
    });
    const layer = new THREE.Points(geo, mat);
    layer.frustumCulled = false;
    group.add(layer);
    disposables.push(geo, mat);
  }

  // --- Layer 3: Near bright stars (400, large, additive glow) ---
  {
    const count = 400;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 800 + Math.random() * 1200;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 4.0,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.95,
      map: getGlowTexture(),
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    const layer = new THREE.Points(geo, mat);
    layer.frustumCulled = false;
    group.add(layer);
    disposables.push(geo, mat);
  }

  // --- Nebula clouds (150 large faint sprite-points, flattened disk) ---
  {
    const count = 150;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const nebulaTints = [
      [0.55, 0.3, 0.8],
      [0.8, 0.6, 0.2],
      [0.3, 0.5, 0.85],
      [0.7, 0.3, 0.5],
    ];
    for (let i = 0; i < count; i++) {
      const r = 600 + Math.random() * 3000;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.35;
      pos[i * 3 + 2] = r * Math.cos(phi);
      const tint = nebulaTints[Math.floor(Math.random() * nebulaTints.length)];
      col[i * 3] = tint[0];
      col[i * 3 + 1] = tint[1];
      col[i * 3 + 2] = tint[2];
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    const mat = new THREE.PointsMaterial({
      size: 90,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.05,
      vertexColors: true,
      map: getGlowTexture(),
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    const layer = new THREE.Points(geo, mat);
    layer.frustumCulled = false;
    group.add(layer);
    disposables.push(geo, mat);
  }

  group.userData.__dispose = () => {
    for (const d of disposables) d.dispose();
  };

  scene.add(group);
  return group;
}

// =====================================================
// MAIN COMPONENT
// =====================================================
export default function Orchestration3D({ nodes, events, eventCursor, className, live = false, autoBlossom = false, scoutKeywords, semanticConfig }: Props) {
  const isDarkTheme = useOsDark();
  const isLightTheme = !isDarkTheme;
  const fgRef = useRef<ForceGraphMethods | null>(null);
  const stablePoolRef = useRef<StablePool>({ nodeMap: new Map(), linkMap: new Map() });
  const nodeObjectsRef = useRef<Map<string, THREE.Object3D>>(new Map());
  const particlesGroupRef = useRef<THREE.Group | null>(null);
  const graphWrapRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const starsRef = useRef<THREE.Group | null>(null);
  const bloomPassRef = useRef<any>(null);
  const lastFrameRef = useRef<number>(0);
  const lastHeavyRef = useRef<number>(0);
  const lastPerfRef = useRef<number>(0);
  const visibilityScanRef = useRef<{ ids: string[]; index: number; lastSize: number }>({
    ids: [],
    index: 0,
    lastSize: 0,
  });
  const geometryCacheRef = useRef<Map<string, THREE.BufferGeometry>>(new Map());

  const materialCacheRef = useRef<Map<string, THREE.Material>>(new Map());

  // Stable refs for live/freezeMotion — used inside useCallback([]) accessors so that
  // ForceGraph3D never sees a changed callback reference, which would trigger
  // nodeDataMapper.clear() (destroying + leaking ALL THREE.js node objects).
  const liveRef = useRef(live);
  const scoutKeywordsRef = useRef(scoutKeywords);
  scoutKeywordsRef.current = scoutKeywords;
  liveRef.current = live;

  const getGeometry = (key: string, factory: () => THREE.BufferGeometry) => {
    let geo = geometryCacheRef.current.get(key);
    if (!geo) {
      geo = factory();
      geometryCacheRef.current.set(key, geo);
    }
    return geo;
  };

  const getMaterial = (key: string, factory: () => THREE.Material) => {
    let mat = materialCacheRef.current.get(key);
    if (!mat) {
      mat = factory();
      materialCacheRef.current.set(key, mat);
    }
    return mat;
  };

  function makeNodeObject(node: GraphNode, opts?: { live?: boolean }) {
    const group = new THREE.Group();

    group.userData.__node = {
      id: node.id,
      type: node.type,
      cluster: node.cluster,
      moduleOf: (node as any).moduleOf,
      kbOf: (node as any).kbOf,
    };

    const baseHex = colorForNode(node);
    const size = defaultNodeSize(node);

    const vis: VisCache = {
      cores: [],
      labels: [],
      halos: [],
      glowPoints: [],
      massClouds: [],
      orbits: [],
      queueRings: [],
      orchRings: [],
      orchShells: [],
      toolGlows: [],
      errorGlows: [],
    };
    group.userData.__vis = vis;

    // ==============================
    // LABEL (ALWAYS)
    // ==============================
    const label = makeLabelSprite(String(node.label || node.id));
    label.position.set(0, size + 12, 0);
    label.visible = false;
    group.add(label);
    vis.labels.push(label);

    // ==============================
    // ORCHESTRATOR — Galaxy Core
    // ==============================
    if (node.type === 'orchestrator') {
      const isMain = String(node.id) === 'orchestrator';
      const shouldRenderHeavy = Boolean(isMain && opts?.live);

      // Bright emissive core sphere
      const coreGeo = getGeometry(`orch_core_${size}`, () => new THREE.SphereGeometry(size, 12, 12));
      const coreMat = getMaterial(`orch_core_${baseHex}`, () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(baseHex),
          transparent: true,
          opacity: 0.98,
          depthWrite: false,
        })
      );
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.userData.__isCore = true;
      core.userData.__isOrchCore = true;
      core.renderOrder = 3;
      group.add(core);
      vis.cores.push(core);

      // Outer glow sprite (blooms beautifully)
      const glowSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTexture(),
          color: new THREE.Color('#a855f7'),
          transparent: true,
          opacity: 0.45,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
        })
      );
      const gs = size * 5;
      glowSprite.scale.set(gs, gs, 1);
      glowSprite.renderOrder = 0;
      group.add(glowSprite);
      vis.halos.push(glowSprite);

      // Accretion ring 1 (main, tilted)
      const ring1Geo = getGeometry(`orch_ring1_${size}`, () => new THREE.TorusGeometry(size * 1.8, 0.4, 8, 24));
      const ring1Mat = getMaterial('orch_ring1', () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color('#c084fc'),
          transparent: true,
          opacity: 0.55,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      const ring1 = new THREE.Mesh(ring1Geo, ring1Mat);
      ring1.rotation.x = Math.PI * 0.45;
      ring1.userData.__orchRing1 = true;
      ring1.renderOrder = 2;
      group.add(ring1);
      vis.orchRings.push(ring1);

      if (isMain) {
        // Accretion ring 2 (wider, different tilt)
        const ring2Geo = getGeometry(`orch_ring2_${size}`, () => new THREE.TorusGeometry(size * 2.5, 0.25, 6, 20));
        const ring2Mat = getMaterial('orch_ring2', () =>
          new THREE.MeshBasicMaterial({
            color: new THREE.Color('#a78bfa'),
            transparent: true,
            opacity: 0.3,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        const ring2 = new THREE.Mesh(ring2Geo, ring2Mat);
        ring2.rotation.x = Math.PI * 0.55;
        ring2.rotation.z = Math.PI * 0.15;
        ring2.userData.__orchRing2 = true;
        ring2.renderOrder = 2;
        group.add(ring2);
        vis.orchRings.push(ring2);

        // Wireframe energy shell
        const shellGeo = getGeometry(`orch_shell_${size}`, () => new THREE.SphereGeometry(size * 2.2, 8, 8));
        const shellMat = getMaterial('orch_shell', () =>
          new THREE.MeshBasicMaterial({
            color: new THREE.Color('#7c3aed'),
            wireframe: true,
            transparent: true,
            opacity: 0.12,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        const shell = new THREE.Mesh(shellGeo, shellMat);
        shell.userData.__isOrchShell = true;
        shell.renderOrder = 1;
        group.add(shell);
        vis.orchShells.push(shell);
      }

      // Corona particle system (live main only)
      if (shouldRenderHeavy) {
        const coronaCount = 100;
        const coronaGeo = getGeometry(`orch_corona_${size}`, () => {
          const g = new THREE.BufferGeometry();
          const pos = new Float32Array(coronaCount * 3);
          for (let i = 0; i < coronaCount; i++) {
            const r = (Math.random() * 0.8 + 0.3) * size * 1.6;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
            pos[i * 3 + 1] = r * Math.cos(phi);
            pos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
          }
          g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
          return g;
        });
        const coronaMat = getMaterial('orch_corona', () =>
          new THREE.PointsMaterial({
            map: getGlowTexture(),
            color: new THREE.Color('#c084fc'),
            size: 3.5,
            sizeAttenuation: true,
            transparent: true,
            opacity: 0.3,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        const corona = new THREE.Points(coronaGeo, coronaMat);
        corona.userData.__isOrchCorona = true;
        corona.renderOrder = 1;
        group.add(corona);
        group.userData.__orchCorona = corona;
        group.userData.__orchCortex = corona; // alias for frame loop compat
        group.userData.__orchWisps = corona;
        vis.massClouds.push(corona);
      }

      return group;
    }

    // ==============================
    // BLOSSOM — Nebula Cluster
    // ==============================
    if (node.type === 'blossom') {
      group.userData.__wantsMassCloud = true;

      // Warm amber core
      const coreGeo = getGeometry(`blossom_core_${size}`, () => new THREE.SphereGeometry(size, 10, 10));
      const coreMat = getMaterial(`blossom_core_${baseHex}`, () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(baseHex),
          transparent: true,
          opacity: 0.95,
          depthWrite: false,
        })
      );
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.userData.__isCore = true;
      core.renderOrder = 2;
      group.add(core);
      vis.cores.push(core);

      // Nebula glow sprite (large, warm)
      const glowSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTexture(),
          color: new THREE.Color('#f59e0b'),
          transparent: true,
          opacity: 0.35,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
        })
      );
      const gs = size * 3.5;
      glowSprite.scale.set(gs, gs, 1);
      glowSprite.renderOrder = 0;
      group.add(glowSprite);
      vis.halos.push(glowSprite);

      // Particle halo cloud (40 points orbiting)
      const haloCount = 40;
      const haloGeo = getGeometry(`blossom_halo_${size}`, () => {
        const g = new THREE.BufferGeometry();
        const pos = new Float32Array(haloCount * 3);
        for (let i = 0; i < haloCount; i++) {
          const r = size * (1.2 + Math.random() * 2.0);
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(2 * Math.random() - 1);
          pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
          pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
          pos[i * 3 + 2] = r * Math.cos(phi);
        }
        g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
        return g;
      });
      const haloMat = getMaterial(`blossom_halo_${baseHex}`, () =>
        new THREE.PointsMaterial({
          map: getGlowTexture(),
          color: new THREE.Color(baseHex),
          size: 4,
          sizeAttenuation: true,
          transparent: true,
          opacity: 0.2,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      const halo = new THREE.Points(haloGeo, haloMat);
      halo.userData.__isBlossomHalo = true;
      halo.renderOrder = 1;
      group.add(halo);
      vis.massClouds.push(halo);

      return group;
    }

    // ==============================
    // AGENT — Luminous Orbiting Body
    // ==============================
    if (node.type === 'agent') {
      // Crystalline icosahedron core
      const coreGeo = getGeometry(`agent_core_${size}`, () => new THREE.IcosahedronGeometry(size, 1));
      const coreMat = getMaterial(`agent_core_${baseHex}_${node.error ? 1 : 0}`, () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(baseHex),
          transparent: true,
          opacity: 0.95,
          depthWrite: false,
        })
      );
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.userData.__isCore = true;
      core.renderOrder = 2;
      group.add(core);
      vis.cores.push(core);

      // Blue glow aura
      const glowSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTexture(),
          color: new THREE.Color('#3b82f6'),
          transparent: true,
          opacity: 0.3,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
        })
      );
      const gs = size * 3;
      glowSprite.scale.set(gs, gs, 1);
      glowSprite.renderOrder = 0;
      group.add(glowSprite);
      vis.halos.push(glowSprite);

      // Small orbital ring
      const ringGeo = getGeometry(`agent_ring_${size}`, () => new THREE.TorusGeometry(size * 1.4, 0.15, 6, 18));
      const ringMat = getMaterial('agent_ring', () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color('#93c5fd'),
          transparent: true,
          opacity: 0.25,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = Math.PI * 0.5;
      ring.rotation.z = Math.random() * Math.PI;
      ring.renderOrder = 1;
      group.add(ring);
      vis.orbits.push(ring);

      // Error glow if errored
      if (node.error) {
        const errSprite = new THREE.Sprite(
          new THREE.SpriteMaterial({
            map: getGlowTexture(),
            color: new THREE.Color('#ef4444'),
            transparent: true,
            opacity: 0.4,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        const es = size * 4;
        errSprite.scale.set(es, es, 1);
        group.add(errSprite);
        vis.errorGlows.push(errSprite);
      }

      return group;
    }

    // ==============================
    // KB — Golden Data Satellite
    // ==============================
    if (node.type === 'kb') {
      const coreGeo = getGeometry(`kb_core_${size}`, () => new THREE.OctahedronGeometry(size, 0));
      const coreMat = getMaterial(`kb_core`, () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color('#fcd34d'),
          transparent: true,
          opacity: 0.92,
          depthWrite: false,
        })
      );
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.userData.__isCore = true;
      core.renderOrder = 2;
      group.add(core);
      vis.cores.push(core);

      // Faint glow
      const glowSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTexture(),
          color: new THREE.Color('#fbbf24'),
          transparent: true,
          opacity: 0.2,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
        })
      );
      const gs = size * 2.5;
      glowSprite.scale.set(gs, gs, 1);
      group.add(glowSprite);
      vis.halos.push(glowSprite);

      return group;
    }

    // ==============================
    // TOOL — Emerald Utility
    // ==============================
    if (node.type === 'tool') {
      const coreGeo = getGeometry(`tool_core_${size}`, () => new THREE.SphereGeometry(size, 16, 16));
      const coreMat = getMaterial(`tool_core_${baseHex}`, () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(baseHex),
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
        })
      );
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.userData.__isCore = true;
      core.renderOrder = 2;
      group.add(core);
      vis.cores.push(core);

      const glowSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTexture(),
          color: new THREE.Color('#34d399'),
          transparent: true,
          opacity: 0.22,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
        })
      );
      const gs = size * 2.5;
      glowSprite.scale.set(gs, gs, 1);
      group.add(glowSprite);
      vis.toolGlows.push(glowSprite);

      return group;
    }

    // ==============================
    // PROCESS — Pink Machinery (default fallback)
    // ==============================
    {
      const coreGeo = getGeometry(`proc_core_${size}`, () => new THREE.IcosahedronGeometry(size, 0));
      const coreMat = getMaterial(`proc_core_${baseHex}`, () =>
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(baseHex),
          transparent: true,
          opacity: 0.88,
          depthWrite: false,
        })
      );
      const core = new THREE.Mesh(coreGeo, coreMat);
      core.userData.__isCore = true;
      core.renderOrder = 2;
      group.add(core);
      vis.cores.push(core);

      // Subtle glow point for far-view
      const glowSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTexture(),
          color: new THREE.Color(baseHex),
          transparent: true,
          opacity: 0.15,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
        })
      );
      const gs = size * 2;
      glowSprite.scale.set(gs, gs, 1);
      group.add(glowSprite);
      vis.glowPoints.push(glowSprite);
    }

    return group;
  }

  const frameMathRef = useRef({
    tmp: new THREE.Vector3(),
    orchPos: new THREE.Vector3(),
    lastCamPos: new THREE.Vector3(),
    lastCamQuat: new THREE.Quaternion(),
    forward: new THREE.Vector3(),
    right: new THREE.Vector3(),
    move: new THREE.Vector3(),
  });

  // =====================================================
  // Semantic physics (optional)
  // =====================================================
  const [semanticEnabled, setSemanticEnabled] = useState(() => {
    if (semanticConfig) return Boolean(semanticConfig.enabled);
    try {
      return window.localStorage.getItem('orch3d.semanticEnabled') === '1';
    } catch {
      return false;
    }
  });

  const [semanticG, setSemanticG] = useState(() => {
    if (semanticConfig) return Number(semanticConfig.G);
    try {
      const v = Number(window.localStorage.getItem('orch3d.semanticG'));
      return Number.isFinite(v) ? v : 25;
    } catch {
      return 25;
    }
  });

  const [semanticK, setSemanticK] = useState(() => {
    if (semanticConfig) return Number(semanticConfig.K);
    try {
      const v = Number(window.localStorage.getItem('orch3d.semanticK'));
      return Number.isFinite(v) ? v : 200;
    } catch {
      return 200;
    }
  });

  const [semanticTemp, setSemanticTemp] = useState(() => {
    if (semanticConfig) return Number(semanticConfig.temp);
    try {
      const v = Number(window.localStorage.getItem('orch3d.semanticTemp'));
      return Number.isFinite(v) ? v : 0.02;
    } catch {
      return 0.02;
    }
  });

  const [affinityThreshold, setAffinityThreshold] = useState(() => {
    if (semanticConfig) return Number(semanticConfig.affinityThreshold);
    try {
      const v = Number(window.localStorage.getItem('orch3d.affinityThreshold'));
      return Number.isFinite(v) ? v : 0.05;
    } catch {
      return 0.05;
    }
  });

  const [topK, setTopK] = useState(() => {
    if (semanticConfig) return Number(semanticConfig.topK);
    try {
      const v = Number(window.localStorage.getItem('orch3d.topK'));
      return Number.isFinite(v) ? v : 6;
    } catch {
      return 6;
    }
  });

  const [anchorEnabled, setAnchorEnabled] = useState(() => {
    if (semanticConfig) return Boolean(semanticConfig.anchorEnabled);
    try {
      return window.localStorage.getItem('orch3d.anchorEnabled') !== '0';
    } catch {
      return true;
    }
  });

  const [promptText, setPromptText] = useState(() => {
    if (semanticConfig) return String(semanticConfig.promptText || '');
    try {
      return window.localStorage.getItem('orch3d.promptText') || '';
    } catch {
      return '';
    }
  });

  const nodeEmbeddingsRef = useRef(new Map<string, number[]>());
  const promptEmbeddingRef = useRef<number[] | null>(null);
  const nodeAffinityRef = useRef(new Map<string, Record<string, number>>());

  const [semanticStatus, setSemanticStatus] = useState<{ loading: boolean; error: string | null; affinityNodes: number }>({
    loading: false,
    error: null,
    affinityNodes: 0,
  });

  // If semanticConfig is provided (e.g. from the Run screen), keep local state in sync.
  useEffect(() => {
    if (!semanticConfig) return;
    setSemanticEnabled(Boolean(semanticConfig.enabled));
    setSemanticTemp(Number(semanticConfig.temp));
    setSemanticG(Number(semanticConfig.G));
    setSemanticK(Number(semanticConfig.K));
    setTopK(Number(semanticConfig.topK));
    setAffinityThreshold(Number(semanticConfig.affinityThreshold));
    setAnchorEnabled(Boolean(semanticConfig.anchorEnabled));
    setPromptText(String(semanticConfig.promptText || ''));
  }, [
    semanticConfig?.enabled,
    semanticConfig?.temp,
    semanticConfig?.G,
    semanticConfig?.K,
    semanticConfig?.topK,
    semanticConfig?.affinityThreshold,
    semanticConfig?.anchorEnabled,
    semanticConfig?.promptText,
  ]);

  useEffect(() => {
    if (semanticConfig) return;
    try {
      window.localStorage.setItem('orch3d.semanticEnabled', semanticEnabled ? '1' : '0');
      window.localStorage.setItem('orch3d.semanticG', String(semanticG));
      window.localStorage.setItem('orch3d.semanticK', String(semanticK));
      window.localStorage.setItem('orch3d.semanticTemp', String(semanticTemp));
      window.localStorage.setItem('orch3d.affinityThreshold', String(affinityThreshold));
      window.localStorage.setItem('orch3d.topK', String(topK));
      window.localStorage.setItem('orch3d.anchorEnabled', anchorEnabled ? '1' : '0');
      window.localStorage.setItem('orch3d.promptText', promptText);
    } catch {
      // ignore
    }
  }, [semanticConfig, semanticEnabled, semanticG, semanticK, semanticTemp, affinityThreshold, topK, anchorEnabled, promptText]);

  const cosine = useCallback((a: number[], b: number[]) => {
    let dot = 0;
    let na = 0;
    let nb = 0;
    const len = Math.min(a.length, b.length);
    for (let i = 0; i < len; i++) {
      const av = a[i] || 0;
      const bv = b[i] || 0;
      dot += av * bv;
      na += av * av;
      nb += bv * bv;
    }
    if (na <= 0 || nb <= 0) return 0;
    return dot / (Math.sqrt(na) * Math.sqrt(nb));
  }, []);

  const dynamicMacroNodes = useMemo(() => {
    // Apply semantic physics only to macro nodes (keep internals pinned).
    return (nodes || []).filter((n) => n.type === 'orchestrator' || n.type === 'agent' || n.type === 'blossom');
  }, [nodes]);

  const computeAffinities = useCallback(
    (macro: OrchestrationNode[], topKLocal: number, threshold: number) => {
      const embs = nodeEmbeddingsRef.current;
      const out = new Map<string, Record<string, number>>();

      for (const a of macro) {
        const aEmb = embs.get(String(a.id));
        if (!aEmb) continue;

        const scored: { id: string; s: number }[] = [];
        for (const b of macro) {
          if (String(b.id) === String(a.id)) continue;
          const bEmb = embs.get(String(b.id));
          if (!bEmb) continue;
          const s = cosine(aEmb, bEmb);
          if (s >= threshold) scored.push({ id: String(b.id), s });
        }

        scored.sort((x, y) => y.s - x.s);
        const selected = scored.slice(0, Math.max(0, topKLocal | 0));
        if (!selected.length) continue;

        const rec: Record<string, number> = {};
        for (const it of selected) rec[it.id] = it.s;
        out.set(String(a.id), rec);
      }

      return out;
    },
    [cosine]
  );

  // Compute embeddings + sparse affinities when semantic mode is on.
  useEffect(() => {
    if (!semanticEnabled) return;

    let alive = true;
    const run = async () => {
      setSemanticStatus((s) => ({ ...s, loading: true, error: null }));
      try {
        // 1) node embeddings for macro nodes
        for (const n of dynamicMacroNodes) {
          if (!alive) return;
          const key = String(n.id);
          if (nodeEmbeddingsRef.current.has(key)) continue;
          const text = `${n.label || n.id}${(n as any).description ? `\n${(n as any).description}` : ''}`;
          const emb = await generateEmbedding384(text);
          if (!alive) return;
          if (emb) nodeEmbeddingsRef.current.set(key, emb);
        }

        // 2) prompt embedding
        if (anchorEnabled && promptText.trim().length) {
          const pe = await generateEmbedding384(promptText.trim());
          if (!alive) return;
          promptEmbeddingRef.current = pe;
        } else {
          promptEmbeddingRef.current = null;
        }

        // 3) neighbor affinities
        const baseAff = computeAffinities(dynamicMacroNodes, topK, affinityThreshold);

        // 4) anchor affinities
        const promptEmb = promptEmbeddingRef.current;
        if (anchorEnabled && promptEmb) {
          for (const n of dynamicMacroNodes) {
            const nEmb = nodeEmbeddingsRef.current.get(String(n.id));
            if (!nEmb) continue;
            const s = cosine(nEmb, promptEmb);
            if (s < affinityThreshold) continue;
            const rec = { ...(baseAff.get(String(n.id)) || {}) };
            rec['prompt'] = s;
            baseAff.set(String(n.id), rec);
          }
        }

        nodeAffinityRef.current = baseAff;

        let withAff = 0;
        for (const n of dynamicMacroNodes) {
          if (baseAff.get(String(n.id))) withAff++;
        }

        setSemanticStatus({ loading: false, error: null, affinityNodes: withAff });
      } catch (e: any) {
        const msg = String(e?.message || e || 'Embeddings unavailable');
        if (!alive) return;
        setSemanticStatus({ loading: false, error: msg, affinityNodes: 0 });
        // Fail safe: drop back to deterministic layout.
        setSemanticEnabled(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [semanticEnabled, dynamicMacroNodes, topK, affinityThreshold, anchorEnabled, promptText, computeAffinities, cosine]);

  // Wire/unwire the semantic force
  useEffect(() => {
    const fg = fgRef.current as any;
    if (!fg) return;

    if (semanticEnabled) {
      try {
        // --- KEEP structural skeleton (weak) ---
        const linkForce = fg.d3Force('link');
        if (linkForce?.strength) {
          linkForce.strength(0.015); // weak structural preservation
        }

        const centerForce = fg.d3Force('center');
        if (centerForce?.strength) {
          centerForce.strength(0.02); // gentle recentering
        }

        // --- REMOVE default global charge ---
        fg.d3Force('charge', null);

        // --- ADD semantic force (layered on top) ---
        fg.d3Force(
          'semantic',
          createSemanticForceEngine({
            G: semanticG,          // try 12–18
            K: semanticK,          // try 180–250
            temperature: semanticTemp, // keep low (0.005–0.012)
          })
        );

        // --- Stabilization ---
        fg.d3VelocityDecay?.(0.30);  // slightly higher damping
        fg.d3AlphaDecay?.(0.02);     // converge smoothly
        fg.d3ReheatSimulation?.();

      } catch {
      // ignore
    }
    return;
  }

    // Back to deterministic: remove custom force, lock simulation.
    try {
      fg.d3Force('semantic', null);
      fg.d3AlphaTarget?.(0);
      fg.cooldownTicks?.(0);
      fg.warmupTicks?.(0);
      fg.d3VelocityDecay?.(1);
      fg.d3AlphaMin?.(0);
      fg.d3AlphaDecay?.(1);
      fg.refresh?.();
    } catch {
      // ignore
    }
  }, [semanticEnabled, semanticG, semanticK, semanticTemp]);

  // Stable slot assignments so newly added nodes don't cause existing nodes to shift.

  const mainBlossomSlotsRef = useRef<Map<string, number>>(new Map());
  const nextMainBlossomSlotRef = useRef(0);
  const forkBlossomSlotsRef = useRef<Map<string, Map<string, number>>>(new Map());
  const procSlotsRef = useRef<Map<string, Map<string, number>>>(new Map());
  const agentSlotsRef = useRef<Map<string, Map<string, number>>>(new Map());

  const scoutRef = useRef<{
    state: ScoutState;
    mesh: THREE.Object3D | null;
    pos: THREE.Vector3;
    vel: THREE.Vector3;
  } | null>(null);
  const [graphSize, setGraphSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [isInteracting, setIsInteracting] = useState(false);

  // =====================================================
  // PERF HUD (toggle via hotkey)
  // =====================================================
  const [perfHudEnabled, setPerfHudEnabled] = useState(false);
  // Perf HUD uses a ref + direct DOM writes to avoid React re-renders of the
  // entire 4000+ line component (which causes 50-100ms forced reflows).
  const perfHudRef = useRef<HTMLDivElement | null>(null);

  const perfRef = useRef({
    lastRafAt: 0,
    frames: 0,
    fpsWindowStart: 0,
    fps: 0,
    lastUiAt: 0,
    lastObjectsAt: 0,
    lastObjects: 0,
    memPeak: 0,
    lastEngineTickAt: 0,
    simDtMs: 0,
    lastConsoleAt: 0,
  });

  // PERF HUD sampling loop is attached later (after LOD refs exist).

  // WASD camera movement (FPS-style translate while keeping orbit target in sync)

  const keysDownRef = useRef<Set<string>>(new Set());
  const controlsTuningRef = useRef<{ baseRotateSpeed: number } | null>(null);

  // Tune OrbitControls mouse feel (slower orbit; mild damping)
  useEffect(() => {
    let raf = 0;
    const apply = () => {
      const fg = fgRef.current as any;
      const controls: any = fg?.controls?.();
      if (!controls) {
        raf = requestAnimationFrame(apply);
        return;
      }

      // Store baseline once
      if (!controlsTuningRef.current) {
        controlsTuningRef.current = { baseRotateSpeed: Number(controls.rotateSpeed ?? 1) };
      }

      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.rotateSpeed = 0.55;
      controls.zoomSpeed = 0.85;
      controls.panSpeed = 0.85;
      controls.update?.();
    };

    raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const isEditableTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      const k = e.key;
      const key = k.length === 1 ? k.toLowerCase() : k;
      // Toggle perf HUD: Ctrl+Shift+P (or Cmd+Shift+P on mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && key === 'p') {
        e.preventDefault();
        setPerfHudEnabled((v) => !v);
        return;
      }

      if (['w', 'a', 's', 'd', 'shift'].includes(key)) {
        e.preventDefault();
        keysDownRef.current.add(key);
      }

    };

    const onKeyUp = (e: KeyboardEvent) => {
      const k = e.key;
      const key = k.length === 1 ? k.toLowerCase() : k;
      if (['w', 'a', 's', 'd', 'shift'].includes(key)) {
        e.preventDefault();
        keysDownRef.current.delete(key);
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKeyDown as any);
      window.removeEventListener('keyup', onKeyUp as any);
    };
  }, []);

  // Standalone WASD rAF loop removed: movement is handled inside ForceGraph's render loop.

  // UI toggles
  const [showLabels, setShowLabels] = useState(true);
  const [trailsEnabled, setTrailsEnabled] = useState(false);
  const [trailDamp, setTrailDamp] = useState(0.88);
  const [freezeMotion, setFreezeMotion] = useState(false);
  const freezeMotionRef = useRef(freezeMotion);
  freezeMotionRef.current = freezeMotion;

  const afterimagePassRef = useRef<any>(null);

  // Trails (afterimage). This is the only place trailsEnabled is applied.
  useEffect(() => {
    const fg = fgRef.current as any;
    if (!fg) return;

    let raf = 0;
    let alive = true;

    const apply = () => {
      if (!alive) return;

      const composer: any = fg.postProcessingComposer?.();
      if (!composer) {
        raf = requestAnimationFrame(apply);
        return;
      }

      const passes: any[] = Array.isArray(composer.passes) ? composer.passes : [];

      // Remove existing afterimage pass (if any)
      if (afterimagePassRef.current) {
        const prev = afterimagePassRef.current;
        try {
          composer.removePass?.(prev);
        } catch {
          // ignore
        }
        const idx = passes.indexOf(prev);
        if (idx >= 0) passes.splice(idx, 1);
        afterimagePassRef.current = null;
      }

      if (trailsEnabled) {
        const pass: any = new AfterimagePass();
        pass.__dyadName = 'orch-afterimage';
        // Higher = longer trail
        pass.uniforms.damp.value = clamp(trailDamp, 0.5, 0.98);

        afterimagePassRef.current = pass;

        // Insert before the final pass so we don't break renderToScreen.
        // (If we add AfterimagePass last, it may not render to screen and the scene appears black.)
        const insertAt = Math.max(1, (Array.isArray(composer.passes) ? composer.passes.length : passes.length) - 1);
        if (typeof composer.insertPass === 'function') composer.insertPass(pass, insertAt);
        else if (typeof composer.addPass === 'function') composer.addPass(pass);
        else passes.splice(insertAt, 0, pass);
      }

      // Ensure exactly the last pass renders to screen
      const finalPasses: any[] = Array.isArray(composer.passes) ? composer.passes : passes;
      for (let i = 0; i < finalPasses.length; i++) {
        const p = finalPasses[i];
        if (p && 'renderToScreen' in p) (p as any).renderToScreen = i === finalPasses.length - 1;
      }

      fg.refresh?.();
    };

    raf = requestAnimationFrame(apply);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [trailsEnabled, trailDamp]);

  // Inspect UI
  const [inspectOpen, setInspectOpen] = useState(false);
  const [inspectTitle, setInspectTitle] = useState('');
  const [inspectBody, setInspectBody] = useState('');
  const [inspectMeta, setInspectMeta] = useState<{ kind: 'node' | 'event'; badge?: string; status?: 'success' | 'failure' } | null>(null);

  // Timeline controls
  const maxCursor = useMemo(() => {
    const upper = typeof eventCursor === 'number' ? clamp(eventCursor, 0, events.length) : events.length;
    return upper;
  }, [eventCursor, events.length]);

  const [followLive, setFollowLive] = useState(!!live);
  const [playing, setPlaying] = useState(!live); // saved mode auto-plays; live mode uses followLive
  const [speed, setSpeed] = useState(1);
  const [cursor, setCursor] = useState(0);

  // Sync follow/play mode when live prop changes
  useEffect(() => {
    if (live) {
      setFollowLive(true);
      setPlaying(false);
    } else {
      setFollowLive(false);
      // Auto-start playback when transitioning from live to saved
      setPlaying(true);
    }
  }, [live]);

  useEffect(() => {
    if (followLive) setCursor(maxCursor);
  }, [followLive, maxCursor]);

  useEffect(() => setCursor((c) => clamp(c, 0, maxCursor)), [maxCursor]);

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();

    const tickTimeline = (now: number) => {
      const dt = Math.max(0, (now - last) / 1000);
      last = now;
      setCursor((c) => {
        const next = c + dt * 12 * speed;
        if (next >= maxCursor) return maxCursor;
        return next;
      });
      raf = requestAnimationFrame(tickTimeline);
    };

    raf = requestAnimationFrame(tickTimeline);
    return () => cancelAnimationFrame(raf);
  }, [playing, maxCursor, speed]);

  useEffect(() => {
    if (cursor >= maxCursor && playing) setPlaying(false);
  }, [cursor, maxCursor, playing]);

  // Resize observer
  useEffect(() => {
    const el = graphWrapRef.current;
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      const w = Math.max(0, Math.floor(r.width));
      const h = Math.max(0, Math.floor(r.height));
      // Some browsers/layout moments report 0 during dialog open; keep last non-zero.
      setGraphSize((prev) => ({
        w: w > 0 ? w : prev.w,
        h: h > 0 ? h : prev.h,
      }));
    };

    // Defer the first measurement to the next frame so Radix Dialog has laid out.
    const raf1 = requestAnimationFrame(update);
    const raf2 = requestAnimationFrame(update);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      // Fallback: window resize only
      window.addEventListener('resize', update);
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Partition nodes once per input
  const base = useMemo(() => partitionNodes(nodes), [nodes]);

  // Build links/events for current cursor
  const { linksAll, visibleEvents } = useMemo(() => buildLinkMap(events, Math.floor(cursor)), [events, cursor]);

  // 3D HUD micro-status (visualization-flavored, stable; updates only when the latest visible event changes)
  const conjurePhrase = useMemo(() => {
    const last = visibleEvents[visibleEvents.length - 1];
    if (!last) return 'Stabilizing the scene';

    if (last.kind === 'fork') return 'Spawning a new constellation';
    if (last.kind === 'blossom') return 'Igniting a new nebula';
    if (last.kind === 'process') return 'Weaving stellar orbits';

    if (last.status === 'failure') return 'Tracing a disruption wave';

    // message (default)
    return 'Threading starlight across the galaxy';
  }, [visibleEvents]);

  // LOD + lazy-loaded blossoms state
  const lodRef = useRef<LODLevel>(2);
  const [lodState, setLodState] = useState<LODLevel>(2);

  const loadedBlossomsRef = useRef<Set<string>>(new Set());

  // PERF HUD rAF loop removed: sampling is handled inside ForceGraph's render loop.

  const requestLoadBlossom = useCallback((blossomId: string) => {
    const s = loadedBlossomsRef.current;
    if (s.has(blossomId)) return;
    s.add(blossomId);
  }, []);

  // Full graphData is stable: includes all nodes and links, independent of loaded blossoms.
  const graphDataBase = useMemo(() => buildFullGraph(base, linksAll), [base, linksAll]);

  // In semantic mode, we inject sparse affinity maps + masses onto macro nodes, and (optionally) add a prompt anchor.
  const graphData = useMemo(() => {
    if (!semanticEnabled) return graphDataBase;

    const macroIds = new Set(
      dynamicMacroNodes
        .map((n) => String(n.id))
        // Keep the main orchestrator pinned for LOD + camera stability.
        .filter((id) => id !== 'orchestrator')
    );

    const nodesOut: any[] = (graphDataBase.nodes || []).map((n: any) => ({ ...n }));

    if (anchorEnabled) {
      const hasPrompt = nodesOut.some((n) => String(n.id) === 'prompt');
      if (!hasPrompt) {
        nodesOut.push({
          id: 'prompt',
          type: 'process',
          label: 'Prompt',
          isAnchor: true,
          mass: 200,
          fx: 0,
          fy: 0,
          fz: 0,
          x: 0,
          y: 0,
          z: 0,
        });
      }
    }

    const massFor = (node: any) => {
      const t = String(node?.type || '');
      if (String(node?.id) === 'prompt') return 200;
      if (t === 'orchestrator') return 120;
      if (t === 'blossom') return 95;
      if (t === 'agent') return 70;
      // pinned internals
      return 10;
    };

    for (const n of nodesOut) {
      const id = String(n.id);

      if (id === 'prompt') {
        n.isAnchor = true;
        n.mass = 200;
        n.fx = 0;
        n.fy = 0;
        n.fz = 0;
        if (!Number.isFinite(n.x)) n.x = 0;
        if (!Number.isFinite(n.y)) n.y = 0;
        if (!Number.isFinite(n.z)) n.z = 0;
        continue;
      }

      if (macroIds.has(id)) {
        // Let macro nodes move.
        if (Number.isFinite(n.fx) && !Number.isFinite(n.x)) n.x = n.fx;
        if (Number.isFinite(n.fy) && !Number.isFinite(n.y)) n.y = n.fy;
        if (Number.isFinite(n.fz) && !Number.isFinite(n.z)) n.z = n.fz;
        delete n.fx;
        delete n.fy;
        delete n.fz;

        n.isAnchor = false;
        n.mass = massFor(n);
        n.affinity = nodeAffinityRef.current.get(id) || undefined;
      } else {
        // Keep internals pinned AND exclude them from semantic forces.
        n.isAnchor = true;
        n.mass = massFor(n);
        n.affinity = undefined;
      }
    }

    return { nodes: nodesOut as any, links: graphDataBase.links as any };
  }, [graphDataBase, semanticEnabled, anchorEnabled, dynamicMacroNodes, semanticStatus.affinityNodes]);

  // =====================================================
  // BUFFERED PLAYBACK (semi-live mode)
  // In live mode, queue graphData snapshots and play them back with a
  // ~3s initial buffer so the user sees smooth, pre-positioned frames.
  // In saved/replay mode, pass through immediately (no delay).
  // =====================================================
  const BUFFER_FILL_MS = 3000;
  const BUFFER_MIN_SNAPSHOTS = 4;
  const PLAYBACK_INTERVAL_MS = 800;
  const QUEUE_MAX_LENGTH = 10;

  const [stableGraphData, setStableGraphData] = useState<GraphSnapshot>(() => {
    stablePoolRef.current = { nodeMap: new Map(), linkMap: new Map() };
    return mergeIntoStablePool(graphData, stablePoolRef.current, false);
  });
  const graphQueueRef = useRef<GraphSnapshot[]>([]);
  const bufferPhaseRef = useRef(true);
  const bufferStartRef = useRef(0);
  const playbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveStartedRef = useRef(false);

  // Animation cache: stores raw snapshots captured during live mode for later replay
  const animationCacheRef = useRef<GraphSnapshot[]>([]);
  const replayCursorRef = useRef(0);
  const replayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isReplaying, setIsReplaying] = useState(false);

  // ── Staggered node admission ──
  // Instead of dumping all new nodes into ForceGraph3D at once (which overwhelms
  // ThreeDigest2 and causes 150-300ms main-thread stalls), we maintain an "admitted"
  // set and introduce new nodes gradually — at most MAX_NEW_PER_TICK per drain cycle.
  const MAX_NEW_PER_TICK = 2;
  const ADMISSION_TICK_MS = 1500; // 1.5s between admission ticks
  const ANIMATION_CACHE_MAX = 500; // cap cached frames to prevent unbounded memory growth
  const admittedIdsRef = useRef<Set<string>>(new Set());
  const admissionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Build a filtered GraphSnapshot from pool containing only admitted nodes + valid links.
  const renderFromPool = useCallback((maxNewToAdmit: number) => {
    const pool = stablePoolRef.current;

    // Find unadmitted node IDs
    const unadmitted: string[] = [];
    for (const id of pool.nodeMap.keys()) {
      if (!admittedIdsRef.current.has(id)) unadmitted.push(id);
    }

    // Admit up to maxNewToAdmit
    const toAdmit = unadmitted.slice(0, maxNewToAdmit);
    for (const id of toAdmit) admittedIdsRef.current.add(id);

    // Build filtered graph from admitted nodes
    const nodes: any[] = [];
    const nodeIds = new Set<string>();
    for (const [id, node] of pool.nodeMap) {
      if (admittedIdsRef.current.has(id)) {
        nodes.push(node);
        nodeIds.add(id);
      }
    }
    const links: any[] = [];
    for (const [, link] of pool.linkMap) {
      const src = getLinkEndpointId(link.source);
      const tgt = getLinkEndpointId(link.target);
      if (nodeIds.has(src) && nodeIds.has(tgt)) links.push(link);
    }

    const filtered: GraphSnapshot = { nodes, links };

    if (!semanticEnabled) {
      prePositionNodes(filtered, {
        mainBlossomSlots: mainBlossomSlotsRef.current,
        nextMainBlossomSlot: nextMainBlossomSlotRef,
        forkBlossomSlots: forkBlossomSlotsRef.current,
        procSlots: procSlotsRef.current,
        agentSlots: agentSlotsRef.current,
      });
    }

    // In live mode, bypass React state entirely — push data directly to ForceGraph3D
    // via its imperative API to avoid a full re-render of this 4000+ line component.
    // The React state is only updated for the initial frame and when leaving live mode.
    const fg = fgRef.current as any;
    if (liveRef.current && fg?.graphData) {
      fg.graphData(filtered);
    } else {
      setStableGraphData(filtered);
    }

    const remaining = unadmitted.length - toAdmit.length;

    // If nodes still pending, ensure admission timer is running
    if (remaining > 0) {
      if (!admissionTimerRef.current) {
        admissionTimerRef.current = setInterval(() => {
          const pool2 = stablePoolRef.current;
          let pending = 0;
          for (const id of pool2.nodeMap.keys()) {
            if (!admittedIdsRef.current.has(id)) pending++;
          }
          if (pending > 0) {
            renderFromPool(MAX_NEW_PER_TICK);
          } else {
            clearInterval(admissionTimerRef.current!);
            admissionTimerRef.current = null;
          }
        }, ADMISSION_TICK_MS);
      }
    } else if (admissionTimerRef.current) {
      clearInterval(admissionTimerRef.current);
      admissionTimerRef.current = null;
    }

    return remaining;
  }, [semanticEnabled]);

  const drainOneSnapshot = useCallback(() => {
    const queue = graphQueueRef.current;
    if (!queue.length) return;
    // Skip to latest if queue is overloaded to prevent unbounded latency
    if (queue.length > QUEUE_MAX_LENGTH) {
      const latest = queue[queue.length - 1];
      queue.length = 0;
      queue.push(latest);
    }
    const snapshot = queue.shift()!;
    // Cache the raw snapshot for later replay (capped to prevent unbounded memory growth)
    if (animationCacheRef.current.length < ANIMATION_CACHE_MAX) {
      animationCacheRef.current.push({
        nodes: snapshot.nodes.map((n) => ({ ...n })),
        links: snapshot.links.map((l) => ({ ...l })),
      });
    }
    // Merge into stable pool (accumulate-only in live mode — same object refs)
    mergeIntoStablePool(snapshot, stablePoolRef.current, true);
    // Render with admission gate — only introduces MAX_NEW_PER_TICK new nodes
    renderFromPool(MAX_NEW_PER_TICK);
  }, [renderFromPool]);

  // Effect 1: Queue ingestion — push snapshots into queue on every graphData change
  useEffect(() => {
    if (!live) {
      // Non-live: passthrough immediately, merge into existing pool (no clearing —
      // clearing would destroy stable object refs and cause geo to flash/disappear).
      graphQueueRef.current.length = 0;
      bufferPhaseRef.current = true;
      liveStartedRef.current = false;
      // Stop any pending admission timer from live mode
      if (admissionTimerRef.current) {
        clearInterval(admissionTimerRef.current);
        admissionTimerRef.current = null;
      }
      // Admit all nodes in non-live mode (cursor controls visibility)
      const merged = mergeIntoStablePool(graphData, stablePoolRef.current, false);
      for (const id of stablePoolRef.current.nodeMap.keys()) {
        admittedIdsRef.current.add(id);
      }
      setStableGraphData(merged);
      return;
    }
    // Initialize buffer phase on first live graphData
    if (!liveStartedRef.current) {
      liveStartedRef.current = true;
      bufferPhaseRef.current = true;
      bufferStartRef.current = Date.now();
      graphQueueRef.current.length = 0;

      // Reset admission + cache for new live session BEFORE admitting initial nodes.
      // (Effect 2 must NOT reset these — it fires in the same render cycle AFTER
      //  Effect 1, which would wipe the IDs we just admitted.）
      admittedIdsRef.current = new Set();
      animationCacheRef.current = [];
      setIsReplaying(false);

      // Show the first snapshot IMMEDIATELY so the orchestrator is visible from frame 1.
      // Admit ALL initial nodes (orchestrator, agents, etc.) — no staggering for the initial frame.
      mergeIntoStablePool(graphData, stablePoolRef.current, true);
      for (const id of stablePoolRef.current.nodeMap.keys()) {
        admittedIdsRef.current.add(id);
      }
      // Cache this first frame
      animationCacheRef.current.push({
        nodes: (graphData.nodes || []).map((n: any) => ({ ...n })),
        links: (graphData.links || []).map((l: any) => ({ ...l })),
      });
      renderFromPool(Infinity); // Admit all — first frame has no limit
      return;
    }
    // Enqueue raw snapshot (shallow-copy arrays; mergeIntoStablePool handles cloning)
    graphQueueRef.current.push({
      nodes: [...(graphData.nodes || [])],
      links: [...(graphData.links || [])],
    });
    // Check if buffer phase should end
    if (bufferPhaseRef.current) {
      const elapsed = Date.now() - bufferStartRef.current;
      if (elapsed >= BUFFER_FILL_MS || graphQueueRef.current.length >= BUFFER_MIN_SNAPSHOTS) {
        bufferPhaseRef.current = false;
        drainOneSnapshot();
      }
    }
  }, [graphData, live, drainOneSnapshot]);

  // Effect 2: Playback interval lifecycle — start/stop the steady drain timer
  useEffect(() => {
    if (!live) {
      // Tear down live mode timers
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      // Stop any pending admission timer from live mode
      if (admissionTimerRef.current) {
        clearInterval(admissionTimerRef.current);
        admissionTimerRef.current = null;
      }
      const queue = graphQueueRef.current;
      if (queue.length) {
        const last = queue[queue.length - 1];
        queue.length = 0;
        const snap = mergeIntoStablePool(last, stablePoolRef.current, false);
        // Admit all remaining nodes when leaving live mode
        for (const id of stablePoolRef.current.nodeMap.keys()) {
          admittedIdsRef.current.add(id);
        }
        if (!semanticEnabled) {
          prePositionNodes(snap, {
            mainBlossomSlots: mainBlossomSlotsRef.current,
            nextMainBlossomSlot: nextMainBlossomSlotRef,
            forkBlossomSlots: forkBlossomSlotsRef.current,
            procSlots: procSlotsRef.current,
            agentSlots: agentSlotsRef.current,
          });
        }
        setStableGraphData(snap);
      }
      return;
    }

    // NOTE: admission/cache reset is handled in Effect 1's init block (liveStartedRef),
    // NOT here — Effect 2 fires in the same render cycle after Effect 1, so resetting
    // here would wipe the IDs that Effect 1 just admitted.

    // Poll until buffer phase ends, then start steady drain interval
    const poller = setInterval(() => {
      if (!bufferPhaseRef.current) {
        clearInterval(poller);
        if (!playbackTimerRef.current) {
          playbackTimerRef.current = setInterval(() => {
            if (graphQueueRef.current.length > 0) {
              drainOneSnapshot();
            }
          }, PLAYBACK_INTERVAL_MS);
        }
      }
    }, 200);
    return () => {
      clearInterval(poller);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      if (admissionTimerRef.current) {
        clearInterval(admissionTimerRef.current);
        admissionTimerRef.current = null;
      }
    };
  }, [live, drainOneSnapshot, semanticEnabled]);

  // Effect 3: Cached animation replay — when transitioning from live to saved,
  // replay the cached frames so the user sees the same animation again.
  useEffect(() => {
    if (live) return;
    if (animationCacheRef.current.length === 0) return;
    if (isReplaying) return; // Already replaying

    // Start replay from cached frames
    replayCursorRef.current = 0;
    stablePoolRef.current = { nodeMap: new Map(), linkMap: new Map() };
    admittedIdsRef.current = new Set();
    setIsReplaying(true);

    const timer = setInterval(() => {
      const cache = animationCacheRef.current;
      const idx = replayCursorRef.current;
      if (idx >= cache.length) {
        clearInterval(timer);
        setIsReplaying(false);
        replayTimerRef.current = null;
        return;
      }
      const snapshot = cache[idx];
      replayCursorRef.current = idx + 1;
      const merged = mergeIntoStablePool(snapshot, stablePoolRef.current, false);
      if (!semanticEnabled) {
        prePositionNodes(merged, {
          mainBlossomSlots: mainBlossomSlotsRef.current,
          nextMainBlossomSlot: nextMainBlossomSlotRef,
          forkBlossomSlots: forkBlossomSlotsRef.current,
          procSlots: procSlotsRef.current,
          agentSlots: agentSlotsRef.current,
        });
      }
      setStableGraphData(merged);
    }, PLAYBACK_INTERVAL_MS);

    replayTimerRef.current = timer;

    return () => {
      clearInterval(timer);
      replayTimerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, semanticEnabled]);

  // Node objects — stable ref ([] deps) so react-kapsule never propagates a new
  // reference, which would trigger nodeDataMapper.clear() and destroy all objects.
  const nodeThreeObject = useCallback(
    (n: any) => {
      const id = String(n?.id);

      if (nodeObjectsRef.current.has(id)) {
        return nodeObjectsRef.current.get(id)!;
      }

      const obj = makeNodeObject(n, { live: liveRef.current });
      nodeObjectsRef.current.set(id, obj);
      return obj;
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // =====================================================
  // MEMOIZED LINK ACCESSORS
  // Inline arrow functions in JSX create new refs every render, causing
  // ForceGraph3D to re-digest all links. Memoizing prevents this churn.
  // In live mode, disable curvature (eliminates per-frame bezier calc +
  // Vector3 allocations) and particles (reduces THREE.js object count).
  // =====================================================
  const linkCurvatureAccessor = useCallback(
    (l: any) => (liveRef.current ? 0 : l.kind === 'process' ? 0.22 : 0.08),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const linkParticlesAccessor = useCallback(
    (l: any) => {
      if (liveRef.current) return 0; // Particles are expensive in live mode
      if (lodRef.current !== 2 || freezeMotionRef.current) return 0;
      if (l.kind === 'blossom') return 2;
      if (l.kind === 'fork') return 2;
      if (l.status === 'failure') return 2;
      return 1;
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const linkParticleWidthAccessor = useCallback(
    (l: any) => (l.status === 'failure' ? 2.5 : 3.5),
    []
  );
  const linkParticleColorAccessor = useCallback(
    (l: any) => colorForLink(l.status),
    []
  );
  const linkParticleSpeedAccessor = useCallback(
    (l: any) => {
      if (liveRef.current) return 0;
      if (lodRef.current !== 2 || freezeMotionRef.current) return 0;
      const latency = clamp(Number(l.latencyMs ?? 800), 40, 8000);
      return clamp(0.0025 * (800 / latency), 0.0006, 0.012);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const linkColorAccessor = useCallback(
    (l: any) => {
      if (lodRef.current === 0) return 'rgba(0,0,0,0)';
      if (lodRef.current === 1) return l.kind === 'blossom' ? colorForLink(l.status, l.kind) : 'rgba(0,0,0,0)';
      if (l.kind === 'process') return '#64748b';
      return colorForLink(l.status, l.kind);
    },
    []
  );
  const linkWidthAccessor = useCallback(
    (l: any) => {
      if (lodRef.current === 0) return 0;
      if (lodRef.current === 1) return l.kind === 'blossom' ? 1.2 : 0;
      if (l.kind === 'process') return lodRef.current === 2 ? 0.8 : 0;
      return l.status === 'failure' ? 2.4 : 1.2;
    },
    []
  );
  const nodeLabelAccessor = useCallback(
    (n: any) => `${n.label} (${n.type})`,
    []
  );
  const nodeValAccessor = useCallback(
    (n: any) => defaultNodeSize(n),
    []
  );

  // Camera helpers
  const getGraphFocus = useCallback(() => {
    const fg = fgRef.current as any;
    const gd = fg?.graphData?.() as any;
    const ns = (gd?.nodes || []) as any[];
    if (!ns.length) return { center: new THREE.Vector3(0, 0, 0), radius: 120 };

    const pts = ns
      .map((n) => ({ x: Number(n.x), y: Number(n.y), z: Number(n.z) }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z));

    if (!pts.length) return { center: new THREE.Vector3(0, 0, 0), radius: 120 };

    let minX = pts[0].x, minY = pts[0].y, minZ = pts[0].z;
    let maxX = pts[0].x, maxY = pts[0].y, maxZ = pts[0].z;
    for (const p of pts) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); minZ = Math.min(minZ, p.z);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); maxZ = Math.max(maxZ, p.z);
    }
    const center = new THREE.Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
    const dx = maxX - minX, dy = maxY - minY, dz = maxZ - minZ;
    const radius = Math.max(80, Math.sqrt(dx * dx + dy * dy + dz * dz) * 0.75);
    return { center, radius };
  }, []);

  const cameraTo = useCallback((pos: THREE.Vector3, lookAt: THREE.Vector3, ms = 450) => {
    const fg = fgRef.current as any;
    if (!fg?.cameraPosition) return;
    fg.cameraPosition({ x: pos.x, y: pos.y, z: pos.z }, { x: lookAt.x, y: lookAt.y, z: lookAt.z }, ms);
  }, []);

  const setViewPreset = useCallback(
    (preset: 'iso' | 'top' | 'front' | 'right') => {
      const { center, radius } = getGraphFocus();
      const d = radius * 2.0;
      if (preset === 'top') return cameraTo(new THREE.Vector3(center.x, center.y + d, center.z + 0.001), center);
      if (preset === 'front') return cameraTo(new THREE.Vector3(center.x, center.y, center.z + d), center);
      if (preset === 'right') return cameraTo(new THREE.Vector3(center.x + d, center.y, center.z), center);
      return cameraTo(new THREE.Vector3(center.x + d * 0.9, center.y + d * 0.65, center.z + d * 0.9), center);
    },
    [cameraTo, getGraphFocus]
  );

  const zoomBy = useCallback(
    (mult: number) => {
      const fg = fgRef.current as any;
      const cam: THREE.PerspectiveCamera | undefined = fg?.camera?.();
      if (!cam) return;
      const { center } = getGraphFocus();
      const from = cam.position.clone();
      const dir = from.sub(center);
      const next = center.clone().add(dir.multiplyScalar(mult));
      cameraTo(next, center, 160);
    },
    [cameraTo, getGraphFocus]
  );

  // Resize ForceGraph renderer
  useEffect(() => {
    const fg = fgRef.current as any;
    if (!fg) return;
    if (!graphSize.w || !graphSize.h) return;

    try {
      if (typeof fg.width === 'function') fg.width(graphSize.w);
      if (typeof fg.height === 'function') fg.height(graphSize.h);

      const cam = fg.camera?.() as THREE.PerspectiveCamera | undefined;
      if (cam && 'aspect' in cam) {
        cam.aspect = graphSize.w / graphSize.h;
        cam.updateProjectionMatrix();
      }
    } catch {
      // ignore
    }
  }, [graphSize.w, graphSize.h]);

  // =====================================================
  // POSITIONING (deterministic) + "galaxy spread"
  // =====================================================
  useEffect(() => {
    const fg = fgRef.current as any;
    if (!fg) return;
    if (semanticEnabled) return;

    // Ensure camera far plane supports starfield
    const cam = fg.camera?.();
    if (cam && cam.far < 20000) {
      cam.far = 20000;
      cam.updateProjectionMatrix();
    }

    let cancelled = false;

    const mulberry32 = (a: number) => {
      return () => {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    };

    const getOrAssignSlot = (map: Map<string, number>, key: string, nextRef: { current: number }) => {
      const existing = map.get(key);
      if (typeof existing === 'number') return existing;
      const idx = nextRef.current++;
      map.set(key, idx);
      return idx;
    };

    const getOrAssignChildSlot = (parentMap: Map<string, Map<string, number>>, parentId: string, childId: string) => {
      let m = parentMap.get(parentId);
      if (!m) {
        m = new Map();
        parentMap.set(parentId, m);
      }
      const existing = m.get(childId);
      if (typeof existing === 'number') return existing;
      const idx = m.size;
      m.set(childId, idx);
      return idx;
    };

    // Compute target position for a node (returns {x,y,z} or null if no position can be determined)
    const computeTargetPosition = (n: any, allNodes: any[], orch: any, blossomNodes: any[]) => {
      const id = String(n.id);
      const type = n.type;

      if (id === 'orchestrator') return { x: 0, y: 0, z: 0 };

      if (type === 'blossom') {
        const isMain = String(n.moduleOf || 'orchestrator') === 'orchestrator';
        if (isMain) {
          const slot = getOrAssignSlot(mainBlossomSlotsRef.current, id, nextMainBlossomSlotRef);
          const SPIRAL_ARMS = 3;
          const ARM_BASE_ANGLE = (Math.PI * 2) / SPIRAL_ARMS;
          const GOLDEN_ANGLE = 2.39996323;
          const arm = slot % SPIRAL_ARMS;
          const armSlot = Math.floor(slot / SPIRAL_ARMS);
          const angle = ARM_BASE_ANGLE * arm + armSlot * GOLDEN_ANGLE * 0.4;
          const radius = 600 + armSlot * 350;
          return {
            x: Math.cos(angle) * radius,
            z: Math.sin(angle) * radius,
            y: Math.sin(angle * 0.7) * 280 * (0.3 + armSlot * 0.12) + ((slot % 5) - 2) * 80,
          };
        } else {
          // Fork blossom
          const parentId = String(n.moduleOf || 'orchestrator');
          const parent = allNodes.find((p: any) => String(p.id) === parentId);
          if (!parent) return null;
          const slot = getOrAssignChildSlot(forkBlossomSlotsRef.current, parentId, id);
          const seed = hashToSeed(`fork-blossoms:${parentId}`);
          const rng = mulberry32(seed);
          const baseR = 220 + rng() * 80;
          const yJitter = 80;
          const ang = (slot / Math.max(1, 6)) * Math.PI * 2 + rng() * 0.35;
          return {
            x: (parent.fx ?? parent.x ?? 0) + Math.cos(ang) * baseR,
            z: (parent.fz ?? parent.z ?? 0) + Math.sin(ang) * baseR,
            y: (parent.fy ?? parent.y ?? 0) + ((slot % 3) - 1) * (yJitter * 0.35) + (rng() - 0.5) * yJitter,
          };
        }
      }

      if (type === 'orchestrator' && id !== 'orchestrator') {
        const seed = hashToSeed(id);
        const rng = mulberry32(seed ^ 0x9e3779b9);
        const parentId = String(n.moduleOf || '');
        const parent = allNodes.find((p: any) => String(p.id) === parentId) || null;
        const px = Number(parent?.fx ?? parent?.x ?? 0);
        const py = Number(parent?.fy ?? parent?.y ?? 0);
        const pz = Number(parent?.fz ?? parent?.z ?? 0);
        const out = new THREE.Vector3(px, py * 0.35, pz);
        if (out.lengthSq() < 1e-6) {
          const a = rng() * Math.PI * 2;
          out.set(Math.cos(a), 0, Math.sin(a));
        }
        out.normalize();
        const side = new THREE.Vector3().crossVectors(out, new THREE.Vector3(0, 1, 0));
        if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
        side.normalize();
        const outward = 520 + rng() * 260;
        const lateral = (rng() - 0.5) * 320;
        const lift = (rng() - 0.5) * 260;
        return {
          x: px + out.x * outward + side.x * lateral,
          y: py + lift,
          z: pz + out.z * outward + side.z * lateral,
        };
      }

      if (type === 'kb') {
        const parentId = String(n.kbOf || '');
        const parent = blossomNodes.find((p: any) => String(p.id) === parentId);
        if (!parent) return null;
        const o = 26;
        return {
          x: (parent.fx ?? parent.x ?? 0) + o,
          y: (parent.fy ?? parent.y ?? 0) - 12,
          z: (parent.fz ?? parent.z ?? 0) - o,
        };
      }

      if (type === 'process') {
        const parentId = String(n.moduleOf || 'orchestrator');
        const parent = allNodes.find((p: any) => String(p.id) === parentId) || orch;
        const slot = getOrAssignChildSlot(procSlotsRef.current, parentId, id);
        const ring = parent?.type === 'orchestrator' ? 66 : parent?.type === 'blossom' ? 72 : 54;
        const ang = (slot / Math.max(1, 10)) * Math.PI * 2;
        return {
          x: (parent.fx ?? parent.x ?? 0) + Math.cos(ang) * ring,
          y: (parent.fy ?? parent.y ?? 0) - (parent?.type === 'orchestrator' ? 22 : 18) + (slot % 3) * 5,
          z: (parent.fz ?? parent.z ?? 0) + Math.sin(ang) * ring,
        };
      }

      if (type === 'agent') {
        const parts = id.split('/');
        const maybeParentId = parts.length > 1 ? parts[0] : '';
        const parent = (maybeParentId && allNodes.find((p: any) => String(p.id) === maybeParentId)) || orch;
        const parentId = String(parent?.id || 'orchestrator');
        const slot = getOrAssignChildSlot(agentSlotsRef.current, parentId, id);
        const ring = parent?.type === 'orchestrator' ? 102 : parent?.type === 'blossom' ? 94 : 78;
        const ang = (slot / Math.max(1, 10)) * Math.PI * 2;
        return {
          x: (parent.fx ?? parent.x ?? 0) + Math.cos(ang) * ring,
          y: (parent.fy ?? parent.y ?? 0) + 12 + ((slot % 3) - 1) * 6,
          z: (parent.fz ?? parent.z ?? 0) + Math.sin(ang) * ring,
        };
      }

      if (type === 'tool') {
        return { x: 22, y: -6, z: 18 };
      }

      return null;
    };

    if (live) {
      // ── LIVE MODE: Lock simulation, pin all nodes with fx/fy/fz ──
      try {
        fg.d3AlphaTarget?.(0);
        fg.cooldownTicks?.(0);
        fg.warmupTicks?.(0);
        fg.d3VelocityDecay?.(1);
        fg.d3AlphaMin?.(0);
        fg.d3AlphaDecay?.(1);

        // Constrain link distances to prevent links stretching across the galaxy
        const linkForce = fg.d3Force('link');
        if (linkForce) {
          linkForce.distance?.((l: any) => {
            const kind = l?.kind;
            if (kind === 'blossom') return 400;
            if (kind === 'fork') return 350;
            return 80;
          });
          linkForce.strength?.(0.02);
        }
      } catch {
        // ignore
      }

      const applyPositions = () => {
        if (cancelled) return false;
        const data = fg.graphData?.() as any;
        const allNodes = (data?.nodes || []) as any[];
        if (!allNodes.length) return false;

        const orch = allNodes.find((n: any) => String(n.id) === 'orchestrator');
        if (!orch) return false;

        const blossomNodes = allNodes.filter((n: any) => n.type === 'blossom');
        let changed = false;

        for (const n of allNodes) {
          if (Number.isFinite(n.fx) && Number.isFinite(n.fy) && Number.isFinite(n.fz)) continue;
          const pos = computeTargetPosition(n, allNodes, orch, blossomNodes);
          if (!pos) continue;
          changed = true;
          n.fx = pos.x;
          n.fy = pos.y;
          n.fz = pos.z;
        }

        if (changed) {
          for (const n of allNodes) {
            if (Number.isFinite(n.fx) && !Number.isFinite(n.x)) n.x = n.fx;
            if (Number.isFinite(n.fy) && !Number.isFinite(n.y)) n.y = n.fy;
            if (Number.isFinite(n.fz) && !Number.isFinite(n.z)) n.z = n.fz;
          }
        }

        try {
          fg.d3AlphaTarget?.(0);
          fg.cooldownTicks?.(0);
          fg.warmupTicks?.(0);
          fg.d3VelocityDecay?.(1);
          fg.d3AlphaMin?.(0);
          fg.d3AlphaDecay?.(1);
        } catch {
          // ignore
        }

        return true;
      };

      let tries = 0;
      const pump = () => {
        tries++;
        const ok = applyPositions();
        if (!ok && tries < 60) requestAnimationFrame(pump);
      };
      requestAnimationFrame(pump);

      return () => { cancelled = true; };
    }

    // ── NON-LIVE (saved/replay) MODE: Organic d3-force animation ──
    // New nodes start near their parent and animate outward via d3 forces.
    // Only the orchestrator is pinned; everything else floats freely.
    const applyOrganicPositions = () => {
      if (cancelled) return false;
      const data = fg.graphData?.() as any;
      const allNodes = (data?.nodes || []) as any[];
      if (!allNodes.length) return false;

      const orch = allNodes.find((n: any) => String(n.id) === 'orchestrator');
      if (!orch) return false;

      const blossomNodes = allNodes.filter((n: any) => n.type === 'blossom');
      let changed = false;

      // Pin orchestrator at origin
      orch.fx = 0; orch.fy = 0; orch.fz = 0;
      orch.x = 0; orch.y = 0; orch.z = 0;

      for (const n of allNodes) {
        if (String(n.id) === 'orchestrator') continue;
        // Only set initial position for nodes that don't have one yet
        if (Number.isFinite(n.x) && Number.isFinite(n.y) && Number.isFinite(n.z)) continue;

        const pos = computeTargetPosition(n, allNodes, orch, blossomNodes);
        if (!pos) continue;
        changed = true;

        // Find parent position for the "pull from" effect
        const parentId = String(n.moduleOf || n.kbOf || 'orchestrator');
        const parent = allNodes.find((p: any) => String(p.id) === parentId);
        const px = Number(parent?.x ?? parent?.fx ?? 0);
        const py = Number(parent?.y ?? parent?.fy ?? 0);
        const pz = Number(parent?.z ?? parent?.fz ?? 0);

        // Start node near parent (70% parent, 30% target) — it will animate outward
        n.x = px * 0.7 + pos.x * 0.3;
        n.y = py * 0.7 + pos.y * 0.3;
        n.z = pz * 0.7 + pos.z * 0.3;

        // NO fx/fy/fz — let d3 forces animate the node organically
      }

      return changed;
    };

    // Set moderate d3 simulation params for organic animation
    try {
      fg.cooldownTicks?.(300);
      fg.warmupTicks?.(0);
      fg.d3VelocityDecay?.(0.35);
      fg.d3AlphaDecay?.(0.02);
      fg.d3AlphaMin?.(0.001);

      // Configure link force with reasonable distance
      const linkForce = fg.d3Force('link');
      if (linkForce) {
        linkForce.distance?.((l: any) => {
          const kind = l?.kind;
          if (kind === 'blossom') return 400;
          if (kind === 'fork') return 350;
          return 80;
        });
        linkForce.strength?.(0.08);
      }

      // Mild charge repulsion to spread nodes
      fg.d3Force('charge')?.strength?.(-30)?.distanceMax?.(300);
    } catch {
      // ignore
    }

    let tries = 0;
    const pump = () => {
      tries++;
      const changed = applyOrganicPositions();
      if (changed) {
        try { fg.d3ReheatSimulation?.(); } catch { /* ignore */ }
      }
      if (tries < 60) requestAnimationFrame(pump);
    };
    requestAnimationFrame(pump);

    return () => { cancelled = true; };
  }, [stableGraphData.nodes.length, semanticEnabled, live]);

  // Fit on mount / resize
  const didInitCameraRef = useRef(false);
  useEffect(() => {
    const fg = fgRef.current as any;
    if (!fg) return;
    if (!graphSize.w || !graphSize.h) return;
    if (!stableGraphData.nodes.length) return;
    if (didInitCameraRef.current) return;

    // Start in a "Detail"-ish view near the orchestrator so the machinery is visible immediately.
    // (Zoom-to-fit the whole galaxy pushes the camera too far out and hides internals via LOD.）
    didInitCameraRef.current = true;
    try {
      fg.cameraPosition({ x: 520, y: 340, z: 520 }, { x: 0, y: 0, z: 0 }, 0);
      fg.controls?.()?.update?.();
    } catch {
      // ignore
    }
  }, [graphSize.w, graphSize.h, stableGraphData.nodes.length]);

  // =====================================================
  // CINEMATIC SCENE INIT + BLOOM
  // =====================================================
  useEffect(() => {
    const fg = fgRef.current as any;
    if (!fg) return;

    let mounted = true;

    const initScene = () => {
      if (!mounted) return;

      const scene: THREE.Scene | undefined = fg.scene?.();
      const renderer: THREE.WebGLRenderer | undefined = fg.renderer?.();
      const cam: THREE.PerspectiveCamera | undefined = fg.camera?.();
      if (!scene || !renderer || !cam) {
        requestAnimationFrame(initScene);
        return;
      }

      sceneRef.current = scene;

      // Deep space background
      scene.background = new THREE.Color('#020308');

      // Extend camera far plane
      if (Number.isFinite(cam.far) && cam.far < 12000) {
        cam.far = 20000;
        cam.near = Math.min(0.5, cam.near || 0.5);
        cam.updateProjectionMatrix();
      }

      // Kill default lights (we use emissive/additive materials)
      scene.traverse((obj: any) => {
        if (obj.isLight) {
          obj.intensity = 0;
          obj.visible = false;
        }
      });

      scene.fog = null;

      // Cap pixel ratio to avoid rendering at 2-3x resolution on HiDPI displays.
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.toneMappingExposure = 1;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.sortObjects = false;
      renderer.autoClear = true;
      renderer.autoClearDepth = true;

      // Multi-layer starfield + nebula
      starsRef.current = ensureStarfield(scene);

      // --- BLOOM POST-PROCESSING ---
      try {
        const composer: any = fg.postProcessingComposer?.();
        if (composer && !bloomPassRef.current) {
          const w = renderer.domElement.width || 1;
          const h = renderer.domElement.height || 1;
          const bloomPass = new UnrealBloomPass(
            new THREE.Vector2(w, h),
            1.0,
            0.4,
            0.2
          );
          bloomPass.renderToScreen = false;

          const passes: any[] = Array.isArray(composer.passes) ? composer.passes : [];
          if (typeof composer.insertPass === 'function') {
            composer.insertPass(bloomPass, 1);
          } else if (typeof composer.addPass === 'function') {
            composer.addPass(bloomPass);
          } else {
            passes.splice(1, 0, bloomPass);
          }

          const finalPasses: any[] = Array.isArray(composer.passes) ? composer.passes : passes;
          for (let i = 0; i < finalPasses.length; i++) {
            const p = finalPasses[i];
            if (p && 'renderToScreen' in p) (p as any).renderToScreen = i === finalPasses.length - 1;
          }

          bloomPassRef.current = bloomPass;
        }
      } catch {
        // Bloom unavailable — scene still looks good without it
      }
    };

    requestAnimationFrame(initScene);
    return () => {
      mounted = false;

      if (bloomPassRef.current) {
        try {
          const composer: any = (fgRef.current as any)?.postProcessingComposer?.();
          composer?.removePass?.(bloomPassRef.current);
        } catch {
          // ignore
        }
        bloomPassRef.current = null;
      }

      const scene = sceneRef.current;
      const stars: any = starsRef.current;
      if (scene && stars) {
        try {
          scene.remove(stars);
        } catch {
          // ignore
        }
      }
      try {
        stars?.userData?.__dispose?.();
      } catch {
        // ignore
      }
      starsRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (bloomPassRef.current) {
      bloomPassRef.current.enabled = !live;
    }
  }, [live]);

  const canPlay = maxCursor > 1;
  const effectiveW = graphSize.w || 900;
  const effectiveH = graphSize.h || 600;
  const backgroundColor = '#020308';
  const hudShellClass = isLightTheme
    ? 'border-border/80 bg-background/92 text-foreground shadow-sm backdrop-blur'
    : 'border-white/10 bg-black/60 text-white/70 backdrop-blur';
  const hudTitleClass = isLightTheme ? 'text-foreground' : 'text-white/90';
  const hudMutedClass = isLightTheme ? 'text-muted-foreground' : 'text-white/60';
  const hudStrongMutedClass = isLightTheme ? 'text-foreground/80' : 'text-white/80';
  const hudTipClass = isLightTheme ? 'text-foreground/70' : 'text-white/70';
  const hudBadgeClass = isLightTheme ? 'border-border/70 bg-muted/40 text-foreground' : 'border-white/15 text-white/80';
  const hudReplayBadgeClass = isLightTheme ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-purple-500/30 text-purple-300';
  const hudOutlineButtonClass = isLightTheme
    ? 'border-border/70 bg-background/90 text-foreground hover:bg-accent hover:text-accent-foreground'
    : 'border-white/15 text-white hover:bg-white/10';
  const hudSecondaryButtonClass = isLightTheme
    ? 'border border-border/70 bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground'
    : undefined;
  const hudSelectClass = isLightTheme
    ? 'h-8 rounded-md border border-border/70 bg-background px-2 text-foreground shadow-sm'
    : 'h-8 rounded-md bg-black/40 border border-white/15 px-2 text-white';
  const perfHudClass = isLightTheme
    ? 'pointer-events-none absolute left-3 top-3 z-20 rounded-md border border-border/80 bg-background/92 backdrop-blur px-3 py-2 text-[11px] font-mono text-foreground/80 leading-snug shadow-sm'
    : 'pointer-events-none absolute left-3 top-3 z-20 rounded-md border border-white/10 bg-black/60 backdrop-blur px-3 py-2 text-[11px] font-mono text-white/80 leading-snug';

  return (
    <div className={cn('h-full w-full flex flex-col overflow-hidden', className)} style={{ background: backgroundColor }}>
      <div className={cn('shrink-0 mx-3 mt-3 rounded-lg pl-3 pr-3 py-2 text-xs', hudShellClass)}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('font-medium', hudTitleClass)}>Orchestration</span>
            {live ? (
              <span className={cn('inline-flex items-center gap-2', hudMutedClass)}>
                <span className={cn('font-medium', hudStrongMutedClass)}>{conjurePhrase}</span>
                <ThinkingDots className={hudMutedClass} />
              </span>
            ) : isReplaying ? (
              <Badge variant="outline" className={hudReplayBadgeClass}>
                Replaying {replayCursorRef.current}/{animationCacheRef.current.length}
              </Badge>
            ) : null}

            <Badge variant="outline" className={hudBadgeClass}>
              {graphData.nodes.length} node(s)
            </Badge>
            <Badge variant="outline" className={hudBadgeClass}>
              {visibleEvents.length}/{events.length} event(s)
            </Badge>
            <Badge variant="outline" className={hudBadgeClass}>
              LOD {lodState === 0 ? 'Galaxy' : lodState === 1 ? 'Regional' : 'Detail'}
            </Badge>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={hudMutedClass}>Labels</span>
              <Switch checked={showLabels} onCheckedChange={setShowLabels} />
            </div>

            <div className="flex items-center gap-2">
              <span className={hudMutedClass}>Trails</span>
              <Switch checked={trailsEnabled} onCheckedChange={setTrailsEnabled} />
            </div>

            <div className="flex items-center gap-2">
              <span className={hudMutedClass}>Freeze</span>
              <Switch checked={freezeMotion} onCheckedChange={setFreezeMotion} />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!live ? (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!canPlay && !isReplaying}
                    onClick={() => {
                      if (isReplaying && replayTimerRef.current) {
                        clearInterval(replayTimerRef.current);
                        replayTimerRef.current = null;
                        setIsReplaying(false);
                        return;
                      }
                      setFreezeMotion(false);
                      setPlaying((p) => !p);
                    }}
                    className={cn('h-8', hudSecondaryButtonClass)}
                  >
                    {(playing || (isReplaying && replayTimerRef.current)) ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    <span className="ml-2 hidden sm:inline">
                      {(playing || (isReplaying && replayTimerRef.current)) ? 'Pause' : 'Play'}
                    </span>
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className={cn('h-8', hudOutlineButtonClass)}
                    disabled={!events.length && animationCacheRef.current.length === 0}
                    onClick={() => {
                      if (replayTimerRef.current) {
                        clearInterval(replayTimerRef.current);
                        replayTimerRef.current = null;
                      }
                      setPlaying(false);
                      setCursor(0);
                      replayCursorRef.current = 0;
                      stablePoolRef.current = { nodeMap: new Map(), linkMap: new Map() };
                      admittedIdsRef.current = new Set();
                      setStableGraphData({ nodes: [], links: [] });
                      setIsReplaying(false);
                    }}
                    title="Replay from start"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>

                  <select
                    className={hudSelectClass}
                    value={String(speed)}
                    onChange={(e) => setSpeed(Number(e.target.value))}
                    title="Playback speed"
                  >
                    <option value="0.5">0.5×</option>
                    <option value="1">1×</option>
                    <option value="1.5">1.5×</option>
                    <option value="2">2×</option>
                  </select>
                </>
              ) : null}

              <div className="flex flex-wrap items-center gap-1.5 pl-1">
                <Button size="icon" variant="outline" className={cn('h-8 w-8', hudOutlineButtonClass)} title="Zoom in" onClick={() => zoomBy(0.82)}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="outline" className={cn('h-8 w-8', hudOutlineButtonClass)} title="Zoom out" onClick={() => zoomBy(1.22)}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className={cn('h-8 w-8', hudOutlineButtonClass)}
                  title="Fit to graph"
                  onClick={() => {
                    try {
                      (fgRef.current as any)?.zoomToFit?.(650, 120);
                    } catch {
                      // ignore
                    }
                  }}
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="outline" className={cn('h-8 w-8', hudOutlineButtonClass)} title="Reset (iso view)" onClick={() => setViewPreset('iso')}>
                  <Home className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" className={cn('h-8 px-2', hudOutlineButtonClass)} title="Top view" onClick={() => setViewPreset('top')}>
                  Top
                </Button>
                <Button size="sm" variant="outline" className={cn('h-8 px-2', hudOutlineButtonClass)} title="Front view" onClick={() => setViewPreset('front')}>
                  Front
                </Button>
                <Button size="sm" variant="outline" className={cn('h-8 px-2', hudOutlineButtonClass)} title="Right view" onClick={() => setViewPreset('right')}>
                  Right
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className={cn('mt-1', hudMutedClass)}>
          Click nodes/links to inspect. Drag to orbit. <span className={hudTipClass}>Tip:</span> turn on{' '}
          <span className={hudStrongMutedClass}>Freeze</span> to click particles. <span className={hudTipClass}>Move:</span> WASD
          (hold Shift to move faster).
        </div>
      </div>

      <div ref={graphWrapRef} className="relative flex-1 min-h-0 w-full">
        {perfHudEnabled ? (
          <div ref={perfHudRef} className={perfHudClass}>
            <div className="flex items-center justify-between gap-6">
              <span className={hudTitleClass}>PERF</span>
              <span className={hudMutedClass}>Ctrl+Shift+P</span>
            </div>
            <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5">
              <div>Nodes: {stableGraphData.nodes.length}</div>
              <div>Links: {stableGraphData.links.length}</div>
              <div>Events: {visibleEvents.length}</div>
              <div>LOD: {lodState}</div>
            </div>
          </div>
        ) : null}

        <ForceGraph3D
          ref={fgRef as any}
          graphData={stableGraphData as any}
          nodeId="id"
          backgroundColor={backgroundColor}
          showNavInfo={false}
          width={effectiveW}
          height={effectiveH}
          nodeLabel={nodeLabelAccessor as any}
          nodeVal={nodeValAccessor as any}
          nodeThreeObject={nodeThreeObject as any}
          nodeThreeObjectExtend={true}
          linkColor={linkColorAccessor as any}
          linkWidth={linkWidthAccessor as any}
          linkCurvature={linkCurvatureAccessor as any}
          linkDirectionalParticles={linkParticlesAccessor as any}
          linkDirectionalParticleWidth={linkParticleWidthAccessor as any}
          linkDirectionalParticleColor={linkParticleColorAccessor as any}
          linkDirectionalParticleSpeed={linkParticleSpeedAccessor as any}
          enableNodeDrag={false}
          onNodeClick={(node: any) => {
            setInspectTitle(String(node?.label || node?.id || 'Node'));
            setInspectBody(JSON.stringify(node, null, 2));
            setInspectMeta({ kind: 'node', badge: String(node?.type || 'node') });
            setInspectOpen(true);
          }}
          onLinkClick={(link: any) => {
            setInspectTitle(`${getLinkEndpointId(link?.source)} → ${getLinkEndpointId(link?.target)}`);
            setInspectBody(JSON.stringify(link, null, 2));
            setInspectMeta({ kind: 'event', badge: String(link?.kind || 'message'), status: link?.status });
            setInspectOpen(true);
          }}
        />
      </div>

      <Dialog open={inspectOpen} onOpenChange={setInspectOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle>{inspectTitle}</DialogTitle>
              {inspectMeta?.badge ? (
                <Badge
                  variant="outline"
                  className={cn(
                    inspectMeta.status === 'failure' && 'border-red-300 text-red-700',
                    inspectMeta.status === 'success' && 'border-emerald-300 text-emerald-700'
                  )}
                >
                  {inspectMeta.badge}
                </Badge>
              ) : null}
            </div>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-4 text-xs text-foreground">
            {inspectBody}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function buildOrchestrationFromTheaterHistory(history: any[]) {
  return inferOrchestrationFromTheaterHistory(history);
}